import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Form, Card, Table, Badge, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaDownload, FaEye, FaEdit, FaCheck, FaTimes, FaTrash, FaUserPlus, FaFileAlt, FaCommentDots, FaInfo, FaCog, FaClipboardCheck, FaCheckCircle, FaEnvelope } from 'react-icons/fa';
import { Eye, Download } from 'lucide-react';
import { getRequisitionDetails, addComment, assignRequisition, amendRequisition, rejectRequisition, sendToJunk, requestSapApproval, requestPMApproval, updateCapexNumber, requestReviewResend } from '../../../models/requisitionModel';
import { getAllActiveUsers } from '../../../models/userModel';
import { saveEvaluation } from '../../../models/quotationModel';
import { getSettings } from '../../../models/settingsModel';
import { parseArraySafely, parseNumericArray } from '../../../utils/phpUnserializer';
import Button from '../../../components/common/Button';
import api from '../../../services/api';
import { API_BASE_URL } from '../../../config';

// Import colors and styles
import { colors, gradients, cardSchemes } from '../../../utils/colors';
import '../../../styles/colors.css';

// Import Modal Components (these exist in the modals folder)
import CommentModal from './modals/CommentModal';
import ApprovalModal from './modals/ApprovalModal';
import RejectModal from './modals/RejectModal';
import JunkModal from './modals/JunkModal';
import AssignModal from './modals/AssignModal';
import AmendModal from './modals/AmendModal';
import FilesModal from './modals/FilesModal';
import AdditionalFilesModal from './modals/AdditionalFilesModal';
import CapexModal from './modals/CapexModal';
import SapApprovalModal from './modals/SapApprovalModal';
import ChangeGLModal from './modals/ChangeGLModal';
import QuoteModal from '../../../components/modals/QuoteModal';
import QuoteViewModal from '../../../components/modals/QuoteViewModal';

// Custom styles using centralized color palette
const customStyles = `
.bg-gradient-primary {
    background: ${gradients.primary} !important;
}
.bg-gradient-info {
    background: ${gradients.cardInfo} !important;
}
.bg-gradient-warning {
    background: ${gradients.cardWarning} !important;
}
.bg-gradient-success {
    background: ${gradients.cardSuccess} !important;
}
.bg-gradient-secondary {
    background: ${gradients.secondary} !important;
}
.compact-card {
    border-radius: 0.5rem !important;
    box-shadow: 0 2px 8px ${colors.ashGray}33;
    border: 1px solid ${colors.antiflashWhite};
}
.compact-header {
    padding: 0.75rem 1rem !important;
    border-bottom: 1px solid ${colors.ashGray};
}
.compact-header, .compact-header h6, .compact-header i, .compact-header * {
    color: #FFFFFF !important;
}
.card-header-white, .card-header-white h6, .card-header-white i, .card-header-white * {
    color: #FFFFFF !important;
}
.compact-header.text-white h6,
.compact-header.text-white * {
    color: white !important;
}
.compact-header.text-dark {
    color: #333 !important;
}
.compact-header.text-dark h6,
.compact-header.text-dark * {
    color: #333 !important;
}
.compact-body {
    padding: 1rem !important;
    background: ${colors.bgCard};
}
.timeline-icon {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: bold;
    color: ${colors.textWhite};
    box-shadow: 0 1px 3px ${colors.steelBlue}33;
}
.timeline-item.completed .timeline-icon {
    background: #28a745 !important;
    box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3);
}
.timeline-item.current .timeline-icon {
    background: #fd7e14 !important;
    animation: pulse 2s infinite;
    box-shadow: 0 2px 6px rgba(253, 126, 20, 0.3);
}
.timeline-item.pending .timeline-icon {
    background: #e9ecef !important;
    color: #adb5bd !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.timeline-connector {
    width: 2px;
    height: 1rem;
    background: ${colors.antiflashWhite};
    margin: 0.25rem auto;
}
.timeline-connector.completed {
    background: ${colors.cambridgeBlue};
}
.info-card {
    transition: all 0.2s ease;
    border: 1px solid ${colors.antiflashWhite};
    background: ${colors.bgCard};
}
.info-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px ${colors.steelBlue}33;
    border-color: ${colors.steelBlue};
}
.compact-timeline-item {
    padding: 0.5rem 0.75rem !important;
}
.compact-button {
    padding: 0.4rem 0.8rem !important;
    font-size: 0.875rem !important;
    transition: all 0.3s ease;
    line-height: 1.3;
}
.compact-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.steelBlue}33;
}
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(253, 126, 20, 0.7); }
    70% { box-shadow: 0 0 0 6px rgba(253, 126, 20, 0); }
    100% { box-shadow: 0 0 0 0 rgba(253, 126, 20, 0); }
}
`;

// Inject styles into head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
}

// Loading Spinner Component
const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const hasEvaluationValues = (values) => {
    if (!values) return false;
    if (Array.isArray(values)) return values.length > 0;
    if (typeof values !== 'object') return false;
    return Boolean(values.quality || values.adherence || values.delivery || values.price);
};

const hasQuotationEvaluation = (quote) => {
    if (!quote || typeof quote !== 'object') return false;
    if (hasEvaluationValues(quote.values)) return true;

    const evaluation = quote.quotation_evaluation;
    if (evaluation === null || evaluation === undefined) return false;

    // Sometimes API returns a JSON string ("{...}" or "[...]"), sometimes an object.
    if (typeof evaluation === 'object') return true;

    if (!isNonEmptyString(evaluation)) return false;
    const trimmed = evaluation.trim();
    if (trimmed === 'null') return false;
    return trimmed.startsWith('{') || trimmed.startsWith('[');
};

const getSelectedQuoteFromRequisition = (data) => {
    const selected = data?.selected;
    if (selected && typeof selected === 'object' && !Array.isArray(selected)) return selected;
    if (Array.isArray(selected) && selected.length > 0) return selected[0];

    const submissions = Array.isArray(data?.submissions) ? data.submissions : [];
    return submissions.find(hasQuotationEvaluation) || submissions[0] || null;
};

const hasAnyQuotationEvaluation = (data) => {
    const submissions = Array.isArray(data?.submissions) ? data.submissions : [];
    if (submissions.some(hasQuotationEvaluation)) return true;
    return hasQuotationEvaluation(getSelectedQuoteFromRequisition(data));
};

// Modern Process Flow Component - Compact design with integrated actions
const ProcessFlowSidebar = ({ data, onAction, canUserApprove, hasPermission, handleApproval, originalUser, authUser, requiresPMApproval, quoteExceedsSiteLimit, globalSettings }) => {
    // Now we have access to globalSettings as a prop
    
    // Check if requisition is junked - if so, show junked workflow
    if (data?.requisition_state == 1) {
        const junkReason = data?.requisition_junk_reason || 'No reason provided';
            
        return (
            <div className="h-full border shadow-sm rounded-lg overflow-hidden" 
                 style={{ 
                     backgroundColor: colors.bgCard,
                     borderColor: colors.antiflashWhite 
                 }}>
                <div className="px-4 py-3 card-header-white" style={{ background: '#6c757d', color: '#FFFFFF' }}>
                    <h6 className="mb-0 font-bold" style={{ color: '#FFFFFF !important', fontSize: '1rem' }}>
                        <i className="fas fa-trash mr-2" style={{ color: '#FFFFFF !important' }}></i>
                        Requisition Junked
                    </h6>
                    <small className="px-2 ty-90" style={{ color: '#FFFFFF !important', opacity: 0.9 }}>Sent to junk</small>
                </div>
                <div className="p-4">
                    <div className="text-center">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"
                             style={{
                                 backgroundColor: '#6c757d20',
                                 color: '#6c757d',
                                 border: '2px solid #6c757d'
                             }}>
                            <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>🗑️</span>
                        </div>
                        <h6 className="font-bold mb-2" style={{ color: '#6c757d', fontSize: '1.1rem' }}>
                            Requisition Junked
                        </h6>
                        <p className="text-sm mb-3" style={{ color: '#495057', lineHeight: '1.5' }}>
                            This requisition has been sent to junk and is no longer active. 
                            It has been removed from the workflow.
                        </p>
                        
                        {/* Show junk reason */}
                        <div className="border rounded-lg p-3 mb-3" 
                             style={{ 
                                 backgroundColor: '#f8f9fa',
                                 borderColor: '#6c757d',
                                 color: '#495057'
                             }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <strong style={{ color: '#6c757d', fontSize: '0.9rem' }}>Junk Reason:</strong>
                            </div>
                            <div style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                {junkReason}
                            </div>
                        </div>
                        
                        <div className="d-flex align-items-center justify-content-center text-sm"
                             style={{ color: '#6c757d' }}>
                            <span>Status: </span>
                            <span className="font-bold ml-1" style={{ color: '#6c757d' }}>Junked</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    // Check if requisition is rejected - if so, show rejection workflow
    if (data?.requisition_status == 4) {
        // Get the latest rejection comment for display - use comment_text like FeedbackSection
        const rejectionComment = data?.comments 
            ? data.comments
                .filter(comment => comment.comment_text && comment.comment_text.includes('REJECTION REASON:'))
                .sort((a, b) => new Date(b.comment_date * 1000) - new Date(a.comment_date * 1000))[0]
            : null;
        
        const rejectionReason = rejectionComment 
            ? rejectionComment.comment_text.replace('REJECTION REASON: ', '').trim()
            : 'No reason provided';
            
        return (
            <div className="h-full border shadow-sm rounded-lg overflow-hidden" 
                 style={{ 
                     backgroundColor: colors.bgCard,
                     borderColor: colors.antiflashWhite 
                 }}>
                <div className="px-4 py-3 card-header-white" style={{ background: '#dc3545', color: '#FFFFFF' }}>
                    <h6 className="mb-0 font-bold" style={{ color: '#FFFFFF !important', fontSize: '1rem' }}>
                        <i className="fas fa-times-circle mr-2" style={{ color: '#FFFFFF !important' }}></i>
                        Workflow Stopped
                    </h6>
                    <small className="px-2 ty-90" style={{ color: '#FFFFFF !important', opacity: 0.9 }}>Requisition rejected</small>
                </div>
                <div className="p-4">
                    <div className="text-center">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"
                             style={{
                                 backgroundColor: '#dc354520',
                                 color: '#dc3545',
                                 border: '2px solid #dc3545'
                             }}>
                            <i className="fas fa-times-circle text-2xl"></i>
                        </div>
                        <h6 className="font-bold mb-2" style={{ color: '#dc3545', fontSize: '1.1rem' }}>
                            Requisition Rejected
                        </h6>
                        <p className="text-sm mb-3" style={{ color: '#495057', lineHeight: '1.5' }}>
                            This requisition has been rejected and the workflow has been stopped. 
                            No further actions can be taken.
                        </p>
                        
                        {/* Show rejection reason */}
                        <div className="border rounded-lg p-3 mb-3" 
                             style={{ 
                                 backgroundColor: '#fff5f5',
                                 borderColor: '#dc3545',
                                 border: '1px solid #dc3545'
                             }}>
                            <div className="text-sm font-medium mb-2" style={{ color: '#dc3545', fontWeight: '600' }}>
                                Rejection Reason:
                            </div>
                            <div className="text-sm" style={{ color: '#212529', fontWeight: '500' }}>
                                {rejectionReason}
                            </div>
                        </div>
                        
                        <div className="border-t pt-3" style={{ borderColor: '#dee2e6' }}>
                            <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                Status: <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Rejected</span>
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    // originalUser is now passed as a prop
    const getStepStatus = (step) => {
        switch (step) {
            case 'quotation':
                // For emergency specifications, need actual quote attachment and submissions
                if (data.specification_emergency == 1) {
                    if (data.requisition_quote && data.submissions && data.submissions.length > 0) {
                        return 'completed';
                    }
                    // Show as current only if this is the first incomplete step
                    return 'current';
                }
                // For non-emergency, just need submissions/quotes
                if (data.submissions && data.submissions.length > 0) {
                    return 'completed';
                }
                // Show as current - this is always the first step
                return 'current';
            case 'evaluation':
                // Check if quotation step is completed first
                const quotationCompleted = data.specification_emergency == 1 
                    ? (data.requisition_quote && data.submissions && data.submissions.length > 0)
                    : (data.submissions && data.submissions.length > 0);
                
                if (!quotationCompleted) {
                    return 'pending';
                }
                
                // Evaluation can live on selected quote OR inside submissions.
                const hasEvaluation = hasAnyQuotationEvaluation(data);
                return hasEvaluation ? 'completed' : 'current';
            case 'declaration':
                // Check if evaluation is completed first
                const quotationCompletedForDecl = data.specification_emergency == 1 
                    ? (data.requisition_quote && data.submissions && data.submissions.length > 0)
                    : (data.submissions && data.submissions.length > 0);
                
                if (!quotationCompletedForDecl) {
                    return 'pending';
                }
                
                const hasEvaluationForDecl = hasAnyQuotationEvaluation(data);
                
                if (!hasEvaluationForDecl) {
                    return 'pending';
                }
                
                return data.requisition_declaration ? 'completed' : 'current';
            case 'pm_approval':
                // Check if declaration is completed first
                if (!data.requisition_declaration) {
                    return 'pending';
                }
                
                // PM approval is only visible when needed (as per requiresPMApproval function)
                // so if we're here, it's definitely needed
                
                // PM approval should ONLY be checked at First Approval stage (status = 0)
                // If status > 0, requisition has moved past PM approval stage
                if (data.requisition_status > 0) {
                    return 'completed';
                }
                
                // If PM approval is approved (value = 1 means approved/done)
                if (data.requisition_pm_approved == 1) {
                    return 'completed';
                }
                
                // If PM approval is pending (value = 1 means request sent, waiting for approval)
                if (data.requisition_pm_approval == 1) {
                    return 'current';
                }
                
                // Need to request PM approval - set as current step
                return 'current';
            case 'first_approval':
                // Check if declaration is completed first
                if (!data.requisition_declaration) {
                    return 'pending';
                }
                
                // If PM approval is required AND not yet approved, first approval should be pending
                // BUT only check this if we're still at status 0 (First Approval stage)
                if (data.requisition_status == 0 && requiresPMApproval(data) && data.requisition_pm_approved != 1) {
                    return 'pending';
                }
                
                // Status 1 = first approval completed
                // Status 0 = waiting for first approval (current)
                if (data.requisition_status >= 1) return 'completed';
                return 'current';
            case 'second_approval':
                // Status 2 = second approval completed
                // Status 1 = first done, waiting for second (current)
                // Status 0 = first not done yet (pending)
                if (data.requisition_status >= 2) return 'completed';
                if (data.requisition_status == 1) return 'current';
                return 'pending';
            case 'third_approval':
                // Status 3 = third approval completed
                // Status 2 = second done, waiting for third (current)
                // Status 0-1 = earlier approvals still needed (pending)
                if (data.requisition_status >= 3) return 'completed';
                if (data.requisition_status == 2) return 'current';
                return 'pending';
            case 'buyer_assigned':
                const isAssignedCompleted = data.assigned;
                const isAssignedCurrent = data.requisition_status >= data.specification_approvals;
                
                return data.assigned ? 'completed' : 
                       (data.requisition_status >= data.specification_approvals ? 'current' : 'pending');
            case 'sap_approval':
                return data.requisition_sap_approve ? 'completed' : 
                       (data.assigned ? 'current' : 'pending');
            case 'purchase_order':
                return data.requisition_po_date > 0 ? 'completed' : 
                       (data.requisition_sap_approve ? 'current' : 'pending');
            default:
                return 'pending';
        }
    };

    // Helper function to get selected quotation total as number
    const getRequisitionTotal = (data) => {
        if (data?.selected?.quotation_total) {
            const cleanTotal = data.selected.quotation_total.toString().replace(/\s+/g, '');
            return parseFloat(cleanTotal) || 0;
        }
        return 0;
    };

    const hasPrerequisiteErrors = (data) => {
        const errors = [];
        
        // Check quotation requirement - matches PHP logic
        if (data.specification_emergency == 1 && !data.requisition_quote) {
            errors.push("Quotation not attached");
        }
        
        // Check if quotation is actually evaluated - selected OR submissions
        const hasEvaluation = hasAnyQuotationEvaluation(data);
        if (!hasEvaluation && data.submissions && data.submissions.length > 0) {
            errors.push("Quotation not evaluated");
        }
        
        if (!data.requisition_declaration) {
            errors.push("Declaration required");
        }
        
        // Check for procurement manager approval requirement
        // Only add error if PM approval is required AND not yet approved
        if (requiresPMApproval(data) && data.requisition_pm_approved != 1) {
            errors.push("Procurement manager approval required");
        }
        
        return errors.length > 0;
    };

    // Get step-specific actions based on current workflow state
    const getStepActions = (stepKey, isCurrent) => {
        if (!isCurrent) return null;

        switch (stepKey) {
            case 'quotation':
                // Show "Attach Now" if quotation needed
                if (data.specification_emergency == 1 && !data.requisition_quote) {
                    return (
                        <Button 
                            variant="warning" 
                            className="compact-button"
                            onClick={onAction.attachQuotation}
                            icon={FaFileAlt}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        >
                            Attach Now
                        </Button>
                    );
                }
                break;

            case 'evaluation':
                // Show "Evaluate Now" if quotation needs evaluation
                const hasEvaluation = hasAnyQuotationEvaluation(data);
                if (!hasEvaluation && data.submissions && data.submissions.length > 0) {
                    return (
                        <Button 
                            variant="warning" 
                            className="compact-button"
                            onClick={onAction.evaluateQuotation}
                            icon={FaClipboardCheck}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        >
                            Evaluate
                        </Button>
                    );
                }
                break;

            case 'declaration':
                // Show "Let Me Declare" if declaration missing
                if (!data.requisition_declaration) {
                    return (
                        <Button 
                            variant="warning" 
                            className="compact-button"
                            onClick={onAction.completeDeclaration}
                            icon={FaEdit}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        >
                            Declare
                        </Button>
                    );
                }
                break;

            case 'pm_approval':
                // Show PM approval actions if required
                if (requiresPMApproval(data)) {
                    // Check if user can approve PM (role 4 OR PM delegate via pm_effective_user_id)
                    const canApprovePM = canUserApprove('pm');
                    if (canApprovePM) {
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="warning" 
                                    className="compact-button"
                                    onClick={onAction.requestCorrection}
                                    icon={FaEdit}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Needs Correction
                                </Button>
                                <Button 
                                    variant="success" 
                                    className="compact-button"
                                    onClick={() => handleApproval('pm')}
                                    icon={FaCheck}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Approve
                                </Button>
                            </div>
                        );
                    }
                    
                    if (data.requisition_pm_approval == 1) {
                        // Request sent, waiting for approval
                        return (
                            <div className="text-center">
                                <span className="px-2 py-1 text-xs font-medium rounded-full border-2" 
                                      style={{
                                          backgroundColor: colors.warning + '10',
                                          color: '#D97706', /* Darker amber/warning for better contrast */
                                          borderColor: colors.warning,
                                          fontWeight: '600',
                                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                      }}>
                                    Request Sent
                                </span>
                            </div>
                        );
                    } else {
                        // Need to request approval
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="warning" 
                                    className="compact-button"
                                    onClick={onAction.requestPMApproval}
                                    icon={FaCheck}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Request Approval
                                </Button>
                                {data.specification_emergency == 1 && (
                                    <Button 
                                        variant="secondary" 
                                        className="compact-button"
                                        onClick={onAction.attachAdditionalQuotation}
                                        icon={FaFileAlt}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        Attach Quote
                                    </Button>
                                )}
                            </div>
                        );
                    }
                }
                break;

            case 'first_approval':
                // Show approval actions for first approver
                // First check if PM approval is needed and not done yet - if so, no actions should be shown
                if (requiresPMApproval(data) && data.requisition_pm_approved != 1) {
                    return null;
                }
                
                if (!hasPrerequisiteErrors(data) && data.requisition_status == 0 && data.mid_manager) {
                    const canApprove = canUserApprove('first');
                    
                    if (canApprove) {
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="warning" 
                                    className="compact-button"
                                    onClick={onAction.requestCorrection}
                                    icon={FaEdit}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Return
                                </Button>
                                <Button 
                                    variant="success" 
                                    className="compact-button"
                                    onClick={() => handleApproval('first')}
                                    icon={FaCheck}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Approve
                                </Button>
                                <Button 
                                    variant="danger" 
                                    className="compact-button text-xs py-1 px-2 w-full min-w-[100px]"
                                    onClick={onAction.reject}
                                    icon={FaTimes}
                                >
                                    Reject
                                </Button>
                            </div>
                        );
                    }
                }
                break;

            case 'second_approval':
   
                // Show approval options for second approver
                if (data.requisition_status == 1 && data.second_approval) {
                    const canApprove = canUserApprove('second');
                    
                    if (canApprove) {
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="warning" 
                                    className="compact-button"
                                    onClick={onAction.requestCorrection}
                                    icon={FaEdit}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Return
                                </Button>
                                <Button 
                                    variant="success" 
                                    className="compact-button"
                                    onClick={() => handleApproval('second')}
                                    icon={FaCheck}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Approve
                                </Button>
                                <Button 
                                    variant="danger" 
                                    className="compact-button text-xs py-1 px-2 w-full min-w-[100px]"
                                    onClick={onAction.reject}
                                    icon={FaTimes}
                                >
                                    Reject
                                </Button>
                            </div>
                        );
                    }
                }
                // Also handle case where no mid manager exists
                else if (data.requisition_status == 0 && !data.mid && data.second_approval) {
                    // Special case for first approval directly to second approver
                    const canApprove = canUserApprove('second');
                    
                    if (canApprove) {
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="warning" 
                                    className="compact-button"
                                    onClick={onAction.requestCorrection}
                                    icon={FaEdit}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%', minWidth: '100px' }}
                                >
                                    Return
                                </Button>
                                <Button 
                                    variant="success" 
                                    className="compact-button"
                                    onClick={() => handleApproval('second')}
                                    icon={FaCheck}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%', minWidth: '100px' }}
                                >
                                    {originalUser ? "Approve*" : "Approve"}
                                </Button>
                                <Button 
                                    variant="danger" 
                                    className="compact-button"
                                    onClick={onAction.reject}
                                    icon={FaTimes}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%', minWidth: '100px' }}
                                >
                                    Reject
                                </Button>
                            </div>
                        );
                    }
                }
                break;

            case 'third_approval':
                // Show approval options for third approver
                // Approver depends on quote value: SAP approver if above limit, PM if at/below limit
                if (data.requisition_status == 2 && data.specification_approvals >= 3) {
                    // Check if we need SAP approver (above limit) or PM (at/below limit)
                    const needsSapApprover = quoteExceedsSiteLimit(data);
                    
                    // Only show actions if the required approver exists
                    if ((needsSapApprover && data.sap_approval) || !needsSapApprover) {
                        const canApprove = canUserApprove('third');
                        
                        if (canApprove) {
                            return (
                                <div className="d-grid gap-1">
                                    <Button 
                                        variant="warning" 
                                        className="compact-button"
                                        onClick={onAction.requestCorrection}
                                        icon={FaEdit}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        Return
                                    </Button>
                                    <Button 
                                        variant="success" 
                                        className="compact-button"
                                        onClick={() => handleApproval('third')}
                                        icon={FaCheck}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                        Approve
                                    </Button>
                                    <Button 
                                        variant="danger" 
                                        className="compact-button text-xs py-1 px-2 w-full min-w-[100px]"
                                        onClick={onAction.reject}
                                        icon={FaTimes}
                                    >
                                        Reject
                                    </Button>
                                </div>
                            );
                        }
                    }
                }
                break;
                
            case 'buyer_assigned':
                // Show assignment actions if current and has permission
                // PM (role 4) and PM delegates can also assign
                const currentUserIdForAssign = Number(authUser?.user_id || 0);
                const pmEffectiveForAssign = Number(data?.pm_effective_user_id || 0);
                const isPMDelegateForAssign = pmEffectiveForAssign > 0 && currentUserIdForAssign === pmEffectiveForAssign;
                const isPMForAssign = authUser?.role == 4 || authUser?.role_id == 4;
                const canAssignByRole = hasPermission("Assign Requisition") || isPMForAssign || isPMDelegateForAssign;
                
                if (data.requisition_status >= data.specification_approvals && 
                    canAssignByRole &&
                    data.requisition_po_date == 0 &&
                    data.requisition_amender_id == 0 &&
                    !data.requisition_sap_approve) {
                    
                    if (!data.assigned) {
                        // Show assign button if not yet assigned
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="primary" 
                                    className="compact-button"
                                    onClick={onAction.assign}
                                    icon={FaUserPlus}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Assign
                                </Button>
                            </div>
                        );
                    } else {
                        // Show re-assign button if already assigned
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant="secondary" 
                                    className="compact-button"
                                    onClick={onAction.reassign}
                                    icon={FaUserPlus}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                >
                                    Re-Assign
                                </Button>
                            </div>
                        );
                    }
                }
                break;

            case 'sap_approval':
                // Show SAP draft request button if buyer is assigned and current user is either:
                // 1. The assigned buyer, OR
                // 2. Any user with role 6 (backup buyer coverage)
                if (data.assigned) {
                    // Find the assigned buyer from the buyers array
                    const assignedBuyer = data.buyers?.find(buyer => buyer.user_full_name === data.assigned);
                    const isAssignedBuyer = assignedBuyer && authUser?.user_id == assignedBuyer.user_id;
                    const isRole6 = authUser?.role == 6 || authUser?.role_id == 6;
                    
                    // Allow action if user is the assigned buyer OR has role 6
                    if (isAssignedBuyer || isRole6) {
                        const hasRequestedSapDraft = data.requisition_sap_approve !== null;
                        
                        return (
                            <div className="d-grid gap-1">
                                <Button 
                                    variant={hasRequestedSapDraft ? "secondary" : "danger"} 
                                    className="compact-button text-xs py-1 px-2 w-full min-w-[100px]"
                                    onClick={onAction.sapApproval}
                                    icon={hasRequestedSapDraft ? FaEdit : FaCheck}
                                >
                                    {hasRequestedSapDraft ? 'Re-request Approval' : 'Request Draft Approval'}
                                </Button>
                            </div>
                        );
                    }
                }
                break;
        }
        return null;
    };

    // Get approver information for display (delegate replaces original approver when set)
    const getApproverInfo = (stepKey) => {
        switch (stepKey) {
            case 'pm_approval':
                // For PM approval, check for delegate first, then PM user
                if (data.pm_delegate && data.pm_user) {
                    return {
                        name: data.pm_delegate.user_full_name,
                        role: `${data.pm_delegate.role_name} (Delegated for ${data.pm_user.user_full_name})`,
                        email: data.pm_delegate.user_email
                    };
                }
                if (data.pm_user) {
                    return {
                        name: data.pm_user.user_full_name,
                        role: data.pm_user.role_name,
                        email: data.pm_user.user_email
                    };
                }
                // Fallback to generic PM info
                return {
                    name: 'Procurement Manager',
                    role: 'Procurement Manager',
                    email: 'N/A'
                };
            case 'first_approval':
                if (data.mid_delegate && data.mid_manager) {
                    return {
                        name: data.mid_delegate.user_full_name,
                        role: `${data.mid_delegate.role_name} (Delegated for ${data.mid_manager.user_full_name})`,
                        email: data.mid_delegate.user_email
                    };
                }
                if (data.mid_manager) {
                    return {
                        name: data.mid_manager.user_full_name,
                        role: data.mid_manager.role_name,
                        email: data.mid_manager.user_email
                    };
                }
                break;
            case 'second_approval':
                if (data.second_delegate && data.second_approval) {
                    return {
                        name: data.second_delegate.user_full_name,
                        role: `${data.second_delegate.role_name} (Delegated for ${data.second_approval.user_full_name})`,
                        email: data.second_delegate.user_email
                    };
                }
                if (data.second_approval) {
                    return {
                        name: data.second_approval.user_full_name,
                        role: data.second_approval.role_name,
                        email: data.second_approval.user_email
                    };
                }
                break;
            case 'third_approval':
                // Determine approver based on quote value
                if (quoteExceedsSiteLimit(data)) {
                    // Above limit: SAP approver
                    if (data.sap_delegate && data.sap_approval) {
                        return {
                            name: data.sap_delegate.user_full_name,
                            role: `${data.sap_delegate.role_name} (High Value, Delegated for ${data.sap_approval.user_full_name})`,
                            email: data.sap_delegate.user_email
                        };
                    }
                    if (data.sap_approval) {
                        return {
                            name: data.sap_approval.user_full_name,
                            role: data.sap_approval.role_name + ' (High Value)',
                            email: data.sap_approval.user_email
                        };
                    }
                } else {
                    // At or below limit: Procurement Manager
                    if (data.pm_delegate && data.pm_user) {
                        return {
                            name: data.pm_delegate.user_full_name,
                            role: `${data.pm_delegate.role_name} (Standard Value, Delegated for ${data.pm_user.user_full_name})`,
                            email: data.pm_delegate.user_email
                        };
                    }
                    if (data.pm_user) {
                        return {
                            name: data.pm_user.user_full_name,
                            role: data.pm_user.role_name + ' (Standard Value)',
                            email: data.pm_user.user_email
                        };
                    }
                    return {
                        name: 'Procurement Manager',
                        role: 'PM Approval (Standard Value)',
                        email: 'N/A'
                    };
                }
                break;
            case 'buyer_assigned':
                if (data.assigned) {
                    return {
                        name: data.assigned,
                        role: 'Buyer',
                        email: data.assigned_email || 'N/A'
                    };
                }
                break;
            case 'sap_approval':
                if (data.assigned) {
                    // Find the assigned buyer from the buyers array
                    const assignedBuyer = data.buyers?.find(buyer => buyer.user_full_name === data.assigned);
                    return {
                        name: data.assigned,
                        role: 'Buyer (SAP Draft Request)',
                        email: assignedBuyer?.user_email || 'N/A'
                    };
                }
                break;
            case 'purchase_order':
                if (data.assigned) {
                    // Find the assigned buyer from the buyers array
                    const assignedBuyer = data.buyers?.find(buyer => buyer.user_full_name === data.assigned);
                    return {
                        name: data.assigned,
                        role: 'Buyer (Purchase Order Creation)',
                        email: assignedBuyer?.user_email || 'N/A'
                    };
                }
                break;
            default:
                return null;
        }
        return null;
    };

    const processSteps = [
        { key: 'quotation', title: 'Quotation', icon: '📄' },
        { key: 'evaluation', title: 'Evaluation', icon: '📊' },
        { key: 'declaration', title: 'Declaration', icon: '✍️' },
        // Add PM approval step if it's required based on the same logic as PHP
        ...(requiresPMApproval(data) 
            ? [{ key: 'pm_approval', title: 'PM Approval', icon: '⚠️', status: 'warning' }] 
            : []
        ),
        { key: 'first_approval', title: 'First Approval', icon: '👤' },
        { key: 'second_approval', title: 'Second Approval', icon: '👥' },
        // Add third approval step if specification_approvals is 3
        ...(data?.specification_approvals >= 3 ? [{ key: 'third_approval', title: 'Third Approval', icon: '👑' }] : []),
        { key: 'buyer_assigned', title: 'Buyer Assigned', icon: '🏢' },
        { key: 'sap_approval', title: 'SAP Draft Approval', icon: '💼' },
        { key: 'purchase_order', title: 'Purchase Order', icon: '📋' }
    ];

    return (
        <div className="h-full border shadow-sm rounded-lg overflow-hidden" 
             style={{ 
                 backgroundColor: colors.bgCard,
                 borderColor: colors.antiflashWhite 
             }}>
            <div className="px-4 py-3 card-header-white" style={{ background: gradients.primary, color: '#FFFFFF' }}>
                <h6 className="mb-0 font-bold" style={{ color: '#FFFFFF !important' }}>
                    <i className="fas fa-route mr-2" style={{ color: '#FFFFFF !important' }}></i>
                    Process Workflow
                </h6>
                <small className="px-2 ty-90" style={{ color: '#FFFFFF !important' }}>Track progress</small>
            </div>
            <div className="p-0">
                <div className="process-timeline">
                    {processSteps.map((step, index) => {
                        const status = getStepStatus(step.key);
                        const isCompleted = status === 'completed';
                        const isCurrent = status === 'current';
                        const isLast = index === processSteps.length - 1;
                        const stepActions = getStepActions(step.key, isCurrent);
                        const approverInfo = getApproverInfo(step.key);
                        
                        return (
                            <div key={step.key} className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                                <div className="py-2 px-3 border-b border-gray-200">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start flex-grow">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0`}
                                                 style={{
                                                     backgroundColor: isCompleted ? '#28a745' : isCurrent ? '#fd7e14' : '#e9ecef',
                                                     color: isCompleted || isCurrent ? colors.textWhite : '#adb5bd'
                                                 }}>
                                                {isCompleted ? '✓' : isCurrent ? '⏳' : step.icon}
                                            </div>
                                            <div className="flex-grow min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`text-sm font-semibold`}
                                                             style={{
                                                                 color: isCompleted ? '#28a745' : isCurrent ? '#fd7e14' : '#adb5bd'
                                                             }}>
                                                            {step.title}
                                                        </div>
                                                        {step.key === 'pm_approval' && (
                                                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded border"
                                                                  style={{
                                                                      backgroundColor: '#fff3cd',
                                                                      color: '#856404',
                                                                      borderColor: '#ffc107'
                                                                  }}>
                                                                Limit-Based
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-3">
                                                        <span className={`px-2 py-1 text-xs font-medium rounded-full border-2 whitespace-nowrap`}
                                                              style={{
                                                                  backgroundColor: isCompleted ? '#d4edda' : isCurrent ? '#fff3cd' : '#f8f9fa',
                                                                  color: isCompleted ? '#28a745' : isCurrent ? '#fd7e14' : '#adb5bd',
                                                                  borderColor: isCompleted ? '#28a745' : isCurrent ? '#fd7e14' : '#e9ecef'
                                                              }}>
                                                            {isCompleted ? 'Done' : isCurrent ? 'Current Step' : 'Pending'}
                                                        </span>
                                                        {isCurrent && hasPrerequisiteErrors(data) && step.key === 'first_approval' && (
                                                            <span className="px-2 py-1 text-xs font-medium rounded-full border-2 whitespace-nowrap" 
                                                                  style={{
                                                                      backgroundColor: colors.danger + '20',
                                                                      color: colors.danger,
                                                                      borderColor: colors.danger
                                                                  }}>Missing</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {approverInfo && (
                                                    <div className="text-xs space-y-0.5 my-1">
                                                        <div className="font-medium" style={{ color: colors.textPrimary }}>
                                                            {approverInfo.name}
                                                        </div>
                                                        <div style={{ color: '#6c757d' }}>{approverInfo.role}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {stepActions && (
                                            <div className="ml-2 flex-shrink-0 flex flex-col space-y-1 mt-0.5" style={{ minWidth: '100px', maxWidth: '120px' }}>
                                                {stepActions}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ViewRequisition = () => {
    const { requisitionId: id } = useParams();
    const navigate = useNavigate();
    const { user: authUser, originalUser, can } = useAuth(); // Add 'can' method from AuthContext
    
    // Add global settings state
    const [globalSettings, setGlobalSettings] = useState({ 
        site_limit: 20000, 
        site_limit_above: '1',
        site_limit_on: '1' // Default to ON for conditional logic
    });
    
    // Load global settings when component mounts
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const response = await getSettings();
                if (response && response.data) {
                    setGlobalSettings(response.data);
                }
            } catch (error) {
                // Error loading global settings
            }
        };
        
        loadSettings();
    }, []);

    // Helper functions - Following the exact PHP logic from requisition_view.php
    const requiresPMApproval = (requisition) => {
        // Early return if requisition data is not available
        if (!requisition) { 
            return false;
        }
        
        // Always use global settings instead of requisition.settings
        const settings = globalSettings;
        
        // Get settings data
        
        // Get the requisition total from the SELECTED quotation (not from requisition_total which is often 0)
        const quotationTotalRaw = requisition.selected?.quotation_total;
        const quotationVatRaw = requisition.selected?.quotation_vat;
        let requisitionTotal = 0;
        let requisitionVat = 0;
        
        if (quotationTotalRaw) {
            // Handle both string and number formats, remove spaces if it's a string
            const cleanTotal = typeof quotationTotalRaw === 'string' 
                ? quotationTotalRaw.replace(/\s/g, '') 
                : quotationTotalRaw.toString();
            requisitionTotal = parseFloat(cleanTotal) || 0;
        }
        
        if (quotationVatRaw) {
            const cleanVat = typeof quotationVatRaw === 'string' 
                ? quotationVatRaw.replace(/\s/g, '') 
                : quotationVatRaw.toString();
            requisitionVat = parseFloat(cleanVat) || 0;
        }
        
        // Calculate net amount (total minus VAT)
        const netAmount = requisitionTotal - requisitionVat;
        
        // Get key values needed for PM approval checks
        const siteLimit = parseFloat(settings.site_limit || 20000);
        const siteLimitAbove = settings.site_limit_above === '1';
        const hasOneSubmission = requisition.submissions?.length === 1;
        const isEmergency = requisition.specification_emergency == 1;
        const isPMApprovalRequested = requisition.requisition_pm_approval == 1;
        const isPMApprovalApproved = requisition.requisition_pm_approved == 1;
   
        
        // Check key values for PM approval
        
        // Must have site_limit_above enabled
        if (!siteLimitAbove) { 
            return false;
        }
        
        // Must be above the limit (using net amount)
        if (netAmount < siteLimit) { 
            return false;
        }
        
        // Must have only one submission (quote)
        if (!hasOneSubmission) { 
            return false;
        }
        
        // Return true to show PM approval step (status handled by getStepStatus)
        return true;
    };

    // Helper function to determine if quote value exceeds site limit (for 3rd approval routing)
    const quoteExceedsSiteLimit = (requisition) => {
        if (!requisition) {
            return false;
        }
        
        const settings = globalSettings;
        const siteLimitOn = settings.site_limit_on === '1';
        
        // If site_limit_on is OFF, always return true (use SAP approver)
        if (!siteLimitOn) {
            return true; // Always exceeds limit = always use SAP approver
        }
        
        // If site_limit_on is ON, check the actual quote value (net amount)
        const siteLimit = parseFloat(settings.site_limit || 20000);
        
        // Get the requisition total and VAT from the SELECTED quotation
        const quotationTotalRaw = requisition.selected?.quotation_total;
        const quotationVatRaw = requisition.selected?.quotation_vat;
        let requisitionTotal = 0;
        let requisitionVat = 0;
        
        if (quotationTotalRaw) {
            const cleanTotal = typeof quotationTotalRaw === 'string' 
                ? quotationTotalRaw.replace(/\s/g, '') 
                : quotationTotalRaw.toString();
            requisitionTotal = parseFloat(cleanTotal) || 0;
        }
        
        if (quotationVatRaw) {
            const cleanVat = typeof quotationVatRaw === 'string' 
                ? quotationVatRaw.replace(/\s/g, '') 
                : quotationVatRaw.toString();
            requisitionVat = parseFloat(cleanVat) || 0;
        }
        
        // Calculate net amount (total minus VAT)
        const netAmount = requisitionTotal - requisitionVat;
        
        return netAmount > siteLimit;
    };

    const getRequisitionTotal = (data) => {
        if (!data?.items) return 0;
        return data.items.reduce((sum, item) => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unit_price) || 0;
            return sum + (quantity * unitPrice);
        }, 0);
    };

    // Helper function to check if current user is the approver at this stage.
    // Delegation is a replacement: if an approver has a delegate, ONLY the delegate should approve.
    const isUserCurrentApprover = (req) => {
        if (!authUser || !authUser.user_id) return false;
        
        const userId = String(authUser.user_id);
        const reqStatus = req.requisition_status;
        
        // Check if it's the user's turn based on status
        if (reqStatus === 0) {
            // First approval stage
            const effectiveApproverId = req.mid_delegate?.user_id ?? req.mid;
            return effectiveApproverId != null && String(effectiveApproverId) === userId;
        } else if (reqStatus === 1) {
            // Second approval stage
            const effectiveApproverId = req.second_delegate?.user_id ?? req.second;
            return effectiveApproverId != null && String(effectiveApproverId) === userId;
        } else if (reqStatus === 2 && req.specification_approvals >= 3) {
            // Third approval stage (only if required)
            // Check if quote exceeds site limit to determine approver type
            const quotationTotal = parseFloat(req.quotation_total?.toString().replace(/[^\d.-]/g, '') || 0);
            const siteLimit = 20000; // Default site limit
            
            if (quotationTotal > siteLimit) {
                // High value: SAP approver
                const effectiveApproverId = req.sap_delegate?.user_id ?? req.approval;
                return effectiveApproverId != null && String(effectiveApproverId) === userId;
            } else {
                // Standard value: PM role (role 4)
                return authUser.role === 4 || authUser.role === '4';
            }
        }
        
        return false;
    };

    // Helper function to check if approver is waiting for more information from requester
    const isApproverWaitingForInfo = (req) => {
        if (!authUser || !authUser.user_id) return false;
        
        // Must be the current approver for this to matter
        if (!isUserCurrentApprover(req)) return false;
        
        // Check if there's a last comment by this approver
        const userId = String(authUser.user_id);
        const lastCommentUser = req.last_comment_user ? String(req.last_comment_user) : null;
        const lastCommentApproved = req.last_comment_approved;
        const lastCommentText = req.last_comment_text || '';
        
        // Return true if:
        // 1. Last comment was made by current user (approver)
        // 2. Last comment was NOT an approval (comment_approved = 0)
        // 3. Last comment was NOT a rejection (doesn't contain rejection keywords)
        if (lastCommentUser === userId && 
            lastCommentApproved === 0 && 
            !lastCommentText.toLowerCase().includes('rejection reason:') &&
            !lastCommentText.toLowerCase().includes('rejected')) {
            return true;
        }
        
        return false;
    };
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0); // Force refresh when masquerading changes
    
    // Watch for user changes (masquerading start/stop) and force refresh
    useEffect(() => {
        setRefreshKey(prev => prev + 1);
    }, [authUser?.user_id, originalUser?.user_id]); // Watch for user ID changes
    
    // Modal states - PRESERVING ALL CRUCIAL MODALS FROM PHP VIEW
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [isRequestingCorrection, setIsRequestingCorrection] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showAmendModal, setShowAmendModal] = useState(false);
    const [showFilesModal, setShowFilesModal] = useState(false);
    const [showAdditionalFilesModal, setShowAdditionalFilesModal] = useState(false);
    const [showCapexModal, setShowCapexModal] = useState(false);
    const [showSapModal, setShowSapModal] = useState(false); 
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [showQuoteViewModal, setShowQuoteViewModal] = useState(false);
    const [showJunkModal, setShowJunkModal] = useState(false);
    const [showChangeGLModal, setShowChangeGLModal] = useState(false);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isEvaluating, setIsEvaluating] = useState(false);

    useEffect(() => {
        fetchRequisition();
        fetchUsers();
    }, [id]);

    const fetchUsers = async () => {
        try {
            const usersData = await getAllActiveUsers();
            const processedUsers = usersData?.data || usersData || [];
            setAllUsers(processedUsers);
        } catch (error) {
            // Error loading users
        }
    };

    const fetchRequisition = async () => {
        try {
            setLoading(true);
            const response = await getRequisitionDetails(id);
            
            if (!response || response.error) {
                throw new Error(response?.error || 'Failed to fetch requisition details');
            }
            
            setData(response);
            setError(null);
        } catch (err) { 
            setError(err.message || 'Failed to fetch requisition');
        } finally {
            setLoading(false);
        }
    };
    
    const canUserApprove = (approvalLevel) => {
 
        if (!authUser) {
            return false;
        }

        // authUser contains the current effective user (masqueraded user when masquerading, normal user when not)
        const currentUserId = Number(authUser.user_id);

        // Use pre-computed effective_user_id fields from API (accounts for delegation)
        switch (approvalLevel) {
            case 'first':
                // mid_effective_user_id: delegate if set, otherwise mid approver
                const midEffective = Number(data?.mid_effective_user_id || data?.mid || 0);
                return midEffective > 0 && currentUserId === midEffective;
                
            case 'second':
                // second_effective_user_id: delegate if set, otherwise second approver
                const secondEffective = Number(data?.second_effective_user_id || data?.second || 0);
                return secondEffective > 0 && currentUserId === secondEffective;
                
            case 'third':
                // For 3rd approval: check if quote exceeds site limit
                if (quoteExceedsSiteLimit(data)) {
                    // Above limit: SAP approver (or their delegate) approves
                    const sapEffective = Number(data?.sap_effective_user_id || data?.approval || 0);
                    return sapEffective > 0 && currentUserId === sapEffective;
                } else {
                    // At or below limit: PM (or PM delegate) approves
                    // Use pm_effective_user_id which accounts for PM delegation
                    const pmEffective = Number(data?.pm_effective_user_id || 0);
                    if (pmEffective > 0 && currentUserId === pmEffective) {
                        return true;
                    }
                    // Fallback: check if user has PM role (role 4)
                    const isPM = authUser?.role == 4 || authUser?.role_id == 4;
                    return isPM;
                }
                
            case 'pm':
                // PM approval: use pm_effective_user_id (accounts for delegation)
                const pmEffectiveForPM = Number(data?.pm_effective_user_id || 0);
                if (pmEffectiveForPM > 0 && currentUserId === pmEffectiveForPM) {
                    return true;
                }
                // Fallback: check if user has PM role (role 4)
                return authUser?.role == 4 || authUser?.role_id == 4;
                
            default:
                return false;
        }
    };

    const hasPermission = (permission) => {
        return can(permission);
    };

    const isOwner = () => {
        return authUser?.user_id == data?.requisition_user;
    };

    const isDepartmentUser = () => {
        return authUser?.department == data?.department_id;
    };

    const isAdmin = () => {  
        return  authUser.role_name == "Buyer";
    };

    // Workflow validation
    const needsQuotation = () => {
        return data?.specification_emergency == 1 && !data?.requisition_quote;
    };

    const needsEvaluation = () => {
        return !hasAnyQuotationEvaluation(data);
    };

    const needsDeclaration = () => {
        return !data?.requisition_declaration;
    };

    const hasErrors = () => {
        return needsQuotation() || needsEvaluation() || needsDeclaration();
    };

    const isFullyApproved = () => {
        const approved = data?.requisition_status >= data?.specification_approvals;
        return approved;
    };

    const canDownload = () => {
        return isFullyApproved();
    };

    const canAssign = () => {
        const fullyApproved = isFullyApproved();
        const hasPermissionCheck = hasPermission("Assign Requisition");
        const notAssigned = !data?.assigned;
        const noSapApproval = !data?.requisition_sap_approve;
        
        // PM delegates (pm_effective_user_id matches current user) can also assign
        const currentUserId = Number(authUser?.user_id || 0);
        const pmEffective = Number(data?.pm_effective_user_id || 0);
        const isPMDelegate = pmEffective > 0 && currentUserId === pmEffective;
        const isPM = authUser?.role == 4 || authUser?.role_id == 4;
        
        const canAssignByRole = hasPermissionCheck || isPM || isPMDelegate;
        const result = fullyApproved && canAssignByRole && notAssigned && noSapApproval;
        
        return result;
    };

    const canReassign = () => {
        const hasPermissionCheck = hasPermission("Assign Requisition");
        const currentUserId = Number(authUser?.user_id || 0);
        const pmEffective = Number(data?.pm_effective_user_id || 0);
        const isPMDelegate = pmEffective > 0 && currentUserId === pmEffective;
        const isPM = authUser?.role == 4 || authUser?.role_id == 4;
        
        return (hasPermissionCheck || isPM || isPMDelegate) && data?.assigned;
    };

    const getCurrentWorkflowStatus = () => {
        if (!data) return 'Loading...';
        
        // Handle special states first
        if (data.requisition_state == 1) return 'Junked';
        if (data.requisition_status == 4) return 'Rejected';
        
        // Handle normal workflow progression
        const isFullyApproved = data.requisition_status >= data.specification_approvals;
        
        // Check if purchase order exists
        if (data.requisition_po_number) {
            return 'Purchase Order Created';
        }
        
        // Check if SAP approval is complete
        if (data.requisition_sap_status == 1) {
            return 'SAP Approved';
        }
        
        // Check if SAP approval is pending/requested
        if (data.requisition_sap_request == 1 || data.requisition_sap_status == 0) {
            return 'SAP Draft Approval';
        }
        
        // Check if buyer is assigned after full approval
        if (isFullyApproved && data.assigned) {
            return `Assigned: ${data.assigned}`;
        }
        
        // Check if fully approved but not assigned
        if (isFullyApproved) {
            return 'Approved';
        }
        
        // Still in approval process
        return 'Pending Approval';
    };

    const handleApproval = async (level) => {
        // Open the approval modal instead of showing a confirmation dialog
        setShowApprovalModal(level);
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!data) return <Alert variant="warning">Requisition not found</Alert>;

    // Quote evaluation handler
    const handleQuoteEvaluation = async (quoteId, evaluation) => {
        try {
            const response = await saveEvaluation(quoteId, evaluation);
            
            if (response.status === 1) {
                // Refresh the data
                await fetchRequisition();
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    };

    const handleGLApproversUpdate = async (glData) => {
        try {
            const response = await api.post('/api/requisitions/update_gl_approvers', glData);
            
            // Check for response.data.status since API returns nested data
            if (response.data && response.data.status === 1) {
                // Refresh the requisition data to show updated GL info
                await fetchRequisition();
                await Swal.fire({
                    title: 'Success!',
                    text: 'GL approvers updated successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
                return true;
            } else {
                await Swal.fire({
                    title: 'Error!',
                    text: 'Failed to update GL approvers: ' + (response.data?.message || response.message || 'Unknown error'),
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
                return false;
            }
        } catch (error) {
            await Swal.fire({
                title: 'Error!',
                text: 'Error updating GL approvers: ' + error.message,
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#dc3545'
            });
            return false;
        }
    };

    const handleEvaluateAction = (action) => {
        if (action === 'toggle') {
            setIsEvaluating(!isEvaluating);
        }
    };

    // Action handlers for workflow - MATCHING PHP FUNCTIONALITY
    const handleActions = {
        comment: () => {
            setIsRequestingCorrection(false);
            setShowCommentModal(true);
        },
        requestCorrection: () => {
            setIsRequestingCorrection(true);
            setShowCommentModal(true);
        },
        reject: () => setShowRejectModal(true),
        assign: () => setShowAssignModal(true),
        reassign: () => setShowAssignModal(true),
        createAmendment: () => setShowAmendModal(true),
        downloadRequisition: () => {
            const token = localStorage.getItem('token');
            const url = `${API_BASE_URL}/api/requisitions/${data.requisition_id}/download${token ? `?token=${token}` : ''}`;
            window.open(url, '_blank');
        },
        viewFiles: () => setShowFilesModal(true),
        capexNumber: () => setShowCapexModal(true),
        changeGL: () => setShowChangeGLModal(true),
        additionalFiles: () => setShowAdditionalFilesModal(true),
        sapApproval: () => setShowSapModal(true), 
        sendToJunk: () => setShowJunkModal(true),
        requestReview: () => setShowReviewModal(true),
        viewQuote: (quote) => {
            setSelectedQuote(quote);
            setShowQuoteModal(true);
        },
        attachQuotation: () => {
            // Redirect to specification page for attachment
            const specificationId = data?.specification_id || data?.requisition_spec_id;
            if (specificationId) {
                navigate(`/dashboard/specifications/${specificationId}`);
            }
        },
        evaluateQuotation: () => {
            // Redirect to specification page for evaluation
            const specificationId = data?.specification_id || data?.requisition_spec_id;
            if (specificationId) {
                navigate(`/dashboard/specifications/${specificationId}`);
            }
        },
        completeDeclaration: () => navigate(`/dashboard/requisitions/edit/${data.requisition_id}`),
        requestPMApproval: async () => {
            try {
                const result = await Swal.fire({
                    title: 'Request Procurement Manager Approval?',
                    text: 'This will send the requisition to the procurement manager for approval.',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, request approval',
                    cancelButtonText: 'Cancel'
                });
                
                if (!result.isConfirmed) return;
                
                await requestPMApproval(data.requisition_id);
                await fetchRequisition(); // Refresh data
                await Swal.fire({
                    title: 'Success!',
                    text: 'Procurement manager approval requested successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                const errorDetails = error.response?.data?.details || '';
                await Swal.fire({
                    title: 'Error!',
                    text: `Failed to request procurement manager approval: ${errorMessage}${errorDetails ? `\nDetails: ${errorDetails}` : ''}`,
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            }
        },
        resendPMApproval: async () => {
            try {
                const result = await Swal.fire({
                    title: 'Resend Procurement Manager Approval?',
                    html: `
                        <p style="margin-bottom: 15px; color: #545454;">This will resend the approval request to the procurement manager.</p>
                        <textarea 
                            id="resend-comment" 
                            placeholder="Add an optional comment or note..."
                            style="
                                width: 100%;
                                height: 120px;
                                padding: 10px;
                                border: 1px solid #d1d5db;
                                border-radius: 6px;
                                font-size: 14px;
                                font-family: inherit;
                                resize: vertical;
                                box-sizing: border-box;
                                margin-top: 10px;
                                outline: none;
                            "
                            onfocus="this.style.borderColor='#3085d6'; this.style.boxShadow='0 0 0 3px rgba(48, 133, 214, 0.1)';"
                            onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none';"
                        ></textarea>
                    `,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, resend approval',
                    cancelButtonText: 'Cancel',
                    width: '500px',
                    preConfirm: () => {
                        const comment = document.getElementById('resend-comment').value;
                        return { comment };
                    }
                });
                
                if (!result.isConfirmed) return;
                
                const comment = result.value?.comment || '';
                await requestReviewResend(data.requisition_id, comment);
                await fetchRequisition(); // Refresh data
                await Swal.fire({
                    title: 'Success!',
                    text: 'Approval request resent successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                const errorDetails = error.response?.data?.details || '';
                await Swal.fire({
                    title: 'Error!',
                    text: `Failed to resend approval request: ${errorMessage}${errorDetails ? `\nDetails: ${errorDetails}` : ''}`,
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            }
        },
        attachAdditionalQuotation: () => {
            // Redirect to specification page, same as Attach and Evaluate
            const specificationId = data?.specification_id || data?.requisition_spec_id;
            if (specificationId) {
                navigate(`/dashboard/specifications/${specificationId}`);
            }
        }
    };

    return (
        <div className="py-2 min-h-screen" style={{ backgroundColor: colors.antiflashWhite }}>
            {/* Compact Header Section */}
            <div className="rounded-lg shadow-sm p-2 mb-3 max-w-7xl mx-auto" style={{ backgroundColor: colors.bgCard }}>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="secondary" 
                                className="compact-button"
                                onClick={() => window.history.back()}
                                icon={FaArrowLeft}
                            >
                                Back
                            </Button>
                            <div className="border-l border-gray-300 h-6"></div>
                            <div>
                                <h5 className="mb-0 font-bold" style={{ color: colors.textPrimary }}>Electronic Purchase Requisition</h5>
                                <small style={{ color: colors.textSecondary }}>EPR-{data?.requisition_number}</small>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flex gap-2">
                            <span className={`px-3 py-1 text-sm font-medium rounded-full border-2`} 
                                  style={{
                                      backgroundColor: (() => {
                                          const status = getCurrentWorkflowStatus();
                                          if (status === 'Junked') return '#6c757d20';
                                          if (status === 'Rejected') return colors.danger + '20';
                                          if (status === 'Purchase Order Created') return colors.success + '20';
                                          if (status === 'SAP Approved') return colors.success + '20';
                                          if (status === 'SAP Draft Approval') return colors.info + '20';
                                          if (status.startsWith('Assigned:')) return colors.primary + '20';
                                          if (status === 'Approved') return colors.success + '20';
                                          return colors.warning + '20';
                                      })(),
                                      color: (() => {
                                          const status = getCurrentWorkflowStatus();
                                          if (status === 'Junked') return '#6c757d';
                                          if (status === 'Rejected') return colors.danger;
                                          if (status === 'Purchase Order Created') return colors.success;
                                          if (status === 'SAP Approved') return colors.success;
                                          if (status === 'SAP Draft Approval') return colors.info;
                                          if (status.startsWith('Assigned:')) return colors.primary;
                                          if (status === 'Approved') return colors.success;
                                          return colors.warning;
                                      })(),
                                      borderColor: (() => {
                                          const status = getCurrentWorkflowStatus();
                                          if (status === 'Junked') return '#6c757d';
                                          if (status === 'Rejected') return colors.danger;
                                          if (status === 'Purchase Order Created') return colors.success;
                                          if (status === 'SAP Approved') return colors.success;
                                          if (status === 'SAP Draft Approval') return colors.info;
                                          if (status.startsWith('Assigned:')) return colors.primary;
                                          if (status === 'Approved') return colors.success;
                                          return colors.warning;
                                      })()
                                  }}>
                                {getCurrentWorkflowStatus()}
                            </span>
                            
                            {/* Awaiting Response Indicator */}
                            {data && isApproverWaitingForInfo(data) && (
                                <span className="px-3 py-1 text-sm font-medium rounded-full border-2" 
                                      style={{
                                          backgroundColor: colors.warning + '20',
                                          color: colors.warning,
                                          borderColor: colors.warning
                                      }}>
                                    Awaiting Response
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Amendment Alert */}
                {data?.requisition_amender_id > 0 && (
                    <div className="mt-3 mb-0 py-3 px-4 rounded-lg border-2" 
                         style={{
                             backgroundColor: colors.danger + '15',
                             borderColor: colors.danger,
                             color: colors.textPrimary
                         }}>
                        <div className="flex items-center">
                            <FaInfo className="mr-2" style={{ color: colors.danger }} />
                            <span className="font-medium">Amended requisition.</span>
                            <Button 
                                variant="primary" 
                                className="ml-3 compact-button"
                                onClick={() => window.location.href = `/dashboard/requisitions/${data.requisition_amender_id}`}
                            >
                                View Amendment
                            </Button>
                        </div>
                    </div>
                )}

                {/* PM Approval Warnings - matching PHP logic exactly */}
                {/* Only show warning if PM approval is required AND not yet approved */}
                {requiresPMApproval(data) && data.requisition_pm_approved != 1 && (
                    <div className="mt-2 mb-0 py-1 px-2 rounded-lg border-2" 
                         style={{
                             backgroundColor: colors.warning + '15',
                             borderColor: colors.warning,
                             color: colors.textPrimary
                         }}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <FaInfo className="mr-3 flex-shrink-0 text-md" style={{ color: colors.warning }} />
                          
                                    <div className="font-medium">
                                        {data.specification_emergency == 1 
                                            ? "Due to the value of your requisition you need to attach another quote or request approval from the procurement manager."
                                            : "Due to the value of your requisition with only 1 quote received, the procurement manager must pre-approve the requisition."
                                        }
                                    </div> 
                            </div>
                            <div className="flex gap-2">

                                {data.requisition_pm_approval == 1 && data.requisition_pm_approved == 0 && (
                                    <>
                                        <div className="px-3 py-2 text-sm font-medium rounded-lg border-2 whitespace-nowrap mt-2 mb-1 inline-flex items-center" 
                                              style={{
                                                  backgroundColor: colors.success + '10',
                                                  color: '#00852c', /* Darker green for better contrast */
                                                  borderColor: colors.success,
                                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                                  fontWeight: '600'
                                              }}>
                                            <FaCheckCircle className="mr-2" />
                                            Approval Request Sent
                                        </div>
                                        <Button 
                                            variant="primary" 
                                            className="compact-button font-medium"
                                            onClick={handleActions.resendPMApproval}
                                        >
                                            Resend
                                        </Button>
                                    </>
                                )}

                                {data.requisition_pm_approval != 1 && (
                                    <>
                                        {data.specification_emergency == 1 && (
                                            <Button 
                                                variant="success" 
                                                className="compact-button font-medium"
                                                onClick={handleActions.attachAdditionalQuotation}
                                            >
                                                Attach Quote
                                            </Button>
                                        )}
                                        <Button 
                                            variant="warning" 
                                            className="compact-button font-medium"
                                            onClick={handleActions.requestPMApproval}
                                        >
                                            Request Approval
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 max-w-7xl mx-auto">
                {/* Left Column - Main Content */}
                <div className="lg:col-span-8">
                    {/* Requisition Details Card */}
                    <div className="border shadow-sm rounded-lg mb-3" 
                         style={{ 
                             backgroundColor: colors.bgCard,
                             borderColor: colors.antiflashWhite 
                         }}>
                        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.primary, color: '#FFFFFF' }}>
                            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                                <i className="fas fa-file-alt mr-2" style={{ color: '#FFFFFF !important' }}></i>
                                Requisition Information
                            </h6>
                        </div>
                        <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex">
                                    <div className="p-3 rounded-lg border flex-1" 
                                         style={{ 
                                             backgroundColor: colors.antiflashWhite,
                                             borderColor: colors.ashGray 
                                         }}>
                                        <div className="text-sm font-medium" style={{ color: colors.textSecondary }}>Requisition Number</div>
                                        <div className="font-bold" style={{ color: colors.textPrimary }}>EPR-{data?.requisition_number}</div>
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="p-3 rounded-lg border flex-1" 
                                         style={{ 
                                             backgroundColor: colors.antiflashWhite,
                                             borderColor: colors.ashGray 
                                         }}>
                                        <div className="text-sm font-medium" style={{ color: colors.textSecondary }}>GL Code</div>
                                        <div className="font-bold" style={{ color: colors.textPrimary }}>{data?.gl_code}</div>
                                        {data?.description && (
                                            <div className="text-xs mt-1" style={{ color: colors.textSecondary }}>{data.description}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex">
                                    <div className="p-3 rounded-lg border flex-1" 
                                         style={{ 
                                             backgroundColor: colors.antiflashWhite,
                                             borderColor: colors.ashGray 
                                         }}>
                                        <div className="text-sm font-medium" style={{ color: colors.textSecondary }}>Division</div>
                                        <div className="font-bold" style={{ color: colors.textPrimary }}>
                                            {data?.department_name || 'Not Set'}
                                        </div>
                                        <div className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                                            Department: {data?.division_name || 'Not Set'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-4 p-4 rounded-lg border" 
                                 style={{ 
                                     backgroundColor: colors.primary + '15',
                                     borderColor: colors.primary 
                                 }}>
                                <h6 className="mb-2 text-sm font-semibold" style={{ color: colors.primary }}>Project Description</h6>
                                <div className="mb-3 font-semibold" style={{ color: colors.textPrimary }} dangerouslySetInnerHTML={{ __html: data?.specification_title }} />
                                {data?.requisition_capex && (
                                    <span className="px-3 py-1 text-sm font-medium rounded-full border" 
                                          style={{
                                              backgroundColor: colors.cambridgeBlue + '20',
                                              color: colors.cambridgeBlue,
                                              borderColor: colors.cambridgeBlue
                                          }}>
                                        Capex: {data.requisition_capex}
                                    </span>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <div className="border-l-4 pl-4" style={{ borderColor: colors.primary }}>
                                        <div className="text-sm font-medium" style={{ color: colors.textSecondary }}>Requested by</div>
                                        <div className="font-bold" style={{ color: colors.textPrimary }}>{data?.user_full_name}</div>
                                        <div className="text-sm" style={{ color: colors.textSecondary }}>
                                            {data?.requisition_date && data.requisition_date !== 'Invalid Date' 
                                                ? new Date(data.requisition_date).toLocaleDateString() 
                                                : 'Date not available'}
                                        </div>
                                    </div>
                                </div>
                                {data?.selected?.quotation_company_name && (
                                    <div>
                                        <div className="border-l-4 pl-4" style={{ borderColor: colors.cambridgeBlue }}>
                                            <div className="text-sm font-medium" style={{ color: colors.textSecondary }}>Selected Supplier</div>
                                            <div className="font-bold" style={{ color: colors.textPrimary }}>{data.selected.quotation_company_name}</div>
                                            <div className="text-sm font-semibold" style={{ color: colors.cambridgeBlue }}>
                                                Total: R {data.selected.quotation_total || '0.00'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Items Specification Card */}
                    <ItemsSpecificationCard data={data} onViewQuote={handleActions.viewQuote} />

                    {/* Specification Details Card */}
                    <SpecificationDetailsCard specificationDetails={data?.specification_spec} />

                    {/* Quotations Summary Card */}
                    {data?.submissions && data.submissions.length > 0 && (
                        <QuotationsSummaryCard 
                            submissions={data.submissions} 
                            selectedQuoteId={data.requisition_quote}
                            onViewQuote={handleActions.viewQuote}
                        />
                    )}

                 
                    {/* Motivation and Declaration */}
                    {(data?.requisition_motivation || data?.requisition_declaration) && (
                        <div className={`grid grid-cols-1 ${data?.requisition_motivation && data?.requisition_declaration ? 'md:grid-cols-2' : ''} gap-4 mb-3`}>
                            {data?.requisition_motivation && (
                                <MotivationCard motivation={data.requisition_motivation} />
                            )}
                            
                            {data?.requisition_declaration && (
                                <DeclarationCard declaration={data.requisition_declaration} />
                            )}
                        </div>
                    )}
                    
                    {/* Supporting Documents */}
                    {data?.files && data.files.length > 0 && (
                        <SupportingDocumentsCard files={data.files} />
                    )}

                    {/* Comments/Feedback Section */}
                    <FeedbackSection comments={data?.comments || []} />
                </div>
                
                {/* Right Column - Process Flow & Actions */}
                <div className="lg:col-span-4">
                    {/* Process Flow Sidebar */}
                    <div className="mb-4">
                        <ProcessFlowSidebar 
                            data={data || {}} 
                            onAction={handleActions}
                            canUserApprove={canUserApprove}
                            hasPermission={hasPermission}
                            handleApproval={handleApproval}
                            originalUser={originalUser}
                            authUser={authUser}
                            requiresPMApproval={requiresPMApproval}
                            quoteExceedsSiteLimit={quoteExceedsSiteLimit}
                            globalSettings={globalSettings}
                        />
                    </div>
                    
                    {/* Action Panel */}
                    <ActionPanel
                        data={data}
                        onAction={handleActions}
                        canUserApprove={canUserApprove}
                        hasPermission={hasPermission}
                        isOwner={isOwner}
                        isDepartmentUser={isDepartmentUser}
                        isAdmin={isAdmin}
                        hasErrors={hasErrors}
                        handleApproval={handleApproval}
                        requiresPMApproval={requiresPMApproval}
                    />
                </div>
            </div>

            {/* ALL CRUCIAL MODALS - PRESERVED FROM PHP */}
            <CommentModal 
                isOpen={showCommentModal}
                onClose={() => {
                    setShowCommentModal(false);
                    setIsRequestingCorrection(false);
                }}
                onSubmit={async (commentData) => {
                    try {
                        const commentPayload = {
                            comment: commentData.comment,
                            additionalInfo: isRequestingCorrection ? true : commentData.additionalInfo,
                            requisition_number: data.requisition_number,
                            requisition_user: data.requisition_user || data.specification_user, // CREATOR's user ID for notifications
                            commenter_name: authUser.user_full_name || authUser.user_name || authUser.name || 'Unknown User',
                            // Add masquerading context
                            original_user_id: originalUser?.user_id || null,
                            is_masquerading: !!originalUser,
                            masqueraded_by: originalUser?.user_id || null, // Fix: originalUser is the one doing the masquerading
                            on_behalf_of: authUser.user_id // Fix: authUser is the one being masqueraded as
                        };
                        
                        await addComment(data.requisition_id, commentPayload);
                        await fetchRequisition();
                        setIsRequestingCorrection(false);
                    } catch (error) {
                        throw error;
                    }
                }}
                requisition={data}
                additionalInfo={isRequestingCorrection}
                currentUser={authUser}
                originalUser={originalUser}
                isMasquerading={!!originalUser}
            />
            
            {/* Review Modal - Request review with optional comment */}
            <CommentModal 
                isOpen={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                title="Resend Approval Email (Optional Comment)"
                onSubmit={async (commentData) => {
                    try {
                        console.log('Review Modal - commentData received:', commentData);
                        console.log('Review Modal - comment value:', commentData.comment);
                        
                        // Save comment if provided
                        if (commentData.comment && commentData.comment.trim()) {
                            const commentPayload = {
                                comment: commentData.comment,
                                additionalInfo: false,
                                requisition_number: data.requisition_number,
                                requisition_user: data.requisition_user || data.specification_user,
                                commenter_name: authUser.user_full_name || authUser.user_name || authUser.name || 'Unknown User',
                                original_user_id: originalUser?.user_id || null,
                                is_masquerading: !!originalUser,
                                masqueraded_by: originalUser?.user_id || null,
                                on_behalf_of: authUser.user_id
                            };
                            await addComment(data.requisition_id, commentPayload);
                        }
                        
                        // Send review request with comment
                        console.log('About to call requestReviewResend with comment:', commentData.comment || '');
                        await requestReviewResend(data.requisition_id, commentData.comment || '');
                        await fetchRequisition();
                        
                        await Swal.fire({
                            title: 'Success!',
                            text: 'Review request has been resent to the current approver.',
                            icon: 'success',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#28a745'
                        });
                    } catch (error) {
                        const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                        await Swal.fire({
                            title: 'Error!',
                            text: `Failed to resend review request: ${errorMessage}`,
                            icon: 'error',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#dc3545'
                        });
                        throw error;
                    }
                }}
                requisition={data}
                additionalInfo={false}
                currentUser={authUser}
                originalUser={originalUser}
                isMasquerading={!!originalUser}
                optional={true}
            />
            
            <RejectModal 
                isOpen={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                onSubmit={async (rejectData) => {
                    try {
                        // First, add the rejection comment to provide context
                        const commentPayload = {
                            comment: `REJECTION REASON: ${rejectData.comment}`,
                            additionalInfo: false,
                            requisition_number: data.requisition_number,
                            requisition_user: authUser.user_id,
                            commenter_name: authUser.user_full_name || authUser.user_name || authUser.name || 'Unknown User',
                            // Add masquerading context
                            original_user_id: originalUser?.user_id || null,
                            is_masquerading: !!originalUser,
                            masqueraded_by: originalUser?.user_id || null,
                            on_behalf_of: authUser.user_id,
                            // Mark this as a rejection comment
                            reject: 1
                        };
                        
                        await addComment(data.requisition_id, commentPayload);
                        
                        // Then reject the requisition (this changes the status)
                        await rejectRequisition(data.requisition_id, rejectData.comment);
                        
                        await fetchRequisition();
                        
                        // Emit custom event to notify other components about the rejection
                        window.dispatchEvent(new CustomEvent('requisitionRejected', {
                            detail: { 
                                requisitionId: data.requisition_id,
                                userId: authUser.user_id,
                                reason: rejectData.comment
                            }
                        }));
                         
                    } catch (error) {
                        throw error;
                    }
                }}
                requisition={data}
                currentUser={authUser}
                originalUser={originalUser}
                isMasquerading={!!originalUser}
            />
            <ApprovalModal
                isOpen={showApprovalModal !== false}
                onClose={() => setShowApprovalModal(false)}
                onSubmit={async (approvalData) => {
                    try {
                        const level = showApprovalModal;
                        let response;
                        
                        // Special handling for PM approval
                        if (level === 'pm') {
                            response = await api.post(`/api/requisitions/${data.requisition_id}/pm_approve`, {
                                requisition_id: data.requisition_id,
                                comment: approvalData.comment,
                                comment_approved: 1
                            });
                            
                            if (response.data.status !== 1) {
                                throw new Error(response.data.message || 'Failed to approve requisition');
                            }
                            
                            // Check if PM is also the first approver
                            const pmUserId = authUser?.user_id || authUser?.id;
                            const firstApproverId = data?.mid;
                            const isPMAlsoFirstApprover = pmUserId && firstApproverId && String(pmUserId) === String(firstApproverId);
                            
                            // Refresh data first to get updated status
                            await fetchRequisition();
                            
                            // Show success message with additional info if PM is also first approver
                            if (isPMAlsoFirstApprover) {
                                await Swal.fire({
                                    title: 'PM Approval Complete!',
                                    html: `
                                        <div class="text-left">
                                            <p class="mb-3">✅ PM approval has been successfully processed.</p>
                                            <div class="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <p class="font-semibold text-blue-900 mb-2">⚠️ Action Required:</p>
                                                <p class="text-blue-800">You are also the <strong>First Approver</strong> for this requisition.</p>
                                                <p class="text-blue-800 mt-2">Please scroll down to the <strong>First Approval</strong> section and approve the requisition again.</p>
                                            </div>
                                        </div>
                                    `,
                                    icon: 'success',
                                    confirmButtonText: 'I Understand',
                                    confirmButtonColor: '#0086D4',
                                    allowOutsideClick: false
                                });
                            } else {
                                await Swal.fire({
                                    title: 'Success!',
                                    text: 'PM approval completed successfully!',
                                    icon: 'success',
                                    confirmButtonText: 'OK',
                                    confirmButtonColor: '#28a745'
                                });
                            }
                            
                            return; // Exit early for PM approval to skip the generic success message
                        } else {
                            // Determine the next approver based on current level and quote value
                            let nextApprover = null;
                            let isRoleBasedApproval = false;
                            let approvalType = 'user'; // 'user' or 'role'
                            let requiredRole = null;
                            
                            if (level === 'first') {
                                nextApprover = data.second;
                            } else if (level === 'second') {
                                // For 2nd approval: check if there's a 3rd approval and who should do it
                                if (data.specification_approvals >= 3) {
                                    // 3rd approval exists - determine if it's SAP or PM based on quote value
                                    if (quoteExceedsSiteLimit(data)) {
                                        // High value: SAP approver (user-based)
                                        nextApprover = data.approval;
                                        approvalType = 'user';
                                    } else {
                                        // Low value: PM (role-based)
                                        nextApprover = 'PM_ROLE';
                                        isRoleBasedApproval = true;
                                        approvalType = 'role';
                                        requiredRole = 4; // PM role ID
                                    }
                                } else {
                                    // No 3rd approval - send null
                                    nextApprover = null;
                                }
                            } else if (level === 'third') {
                                nextApprover = null;
                            } else {
                                nextApprover = data.approval;
                            }
                            
                            // Create the proper payload structure for approval
                            const commentPayload = {
                                comment: approvalData.comment,
                                comment_approved: 1, // Set the approval flag
                                auto_approve_next: approvalData.auto_approve_next, // Pass auto-approval setting
                                additionalInfo: false,
                                requisition_number: data.requisition_number,
                                requisition_user: authUser.user_id,
                                commenter_name: authUser.user_full_name || authUser.user_name || authUser.name || 'Unknown User',
                                // Add masquerading context
                                original_user_id: originalUser?.user_id || null,
                                is_masquerading: !!originalUser,
                                masqueraded_by: originalUser?.user_id || null,
                                on_behalf_of: authUser.user_id,
                                // Add approval-specific fields
                                requisition_title: data.specification_title,
                                gl_description: data.description,
                                second_approval: nextApprover,
                                // Add 3rd approval routing context
                                is_role_based_approval: isRoleBasedApproval,
                                approval_type: approvalType,
                                required_role: requiredRole
                            };
                            
                            // Call addComment with correct parameters for approval
                            const result = await addComment(data.requisition_id, commentPayload);
                            
                            // Show success message
                            await Swal.fire({
                                title: 'Success!',
                                text: result?.data?.message || result?.message || 'Requisition approved successfully!',
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false
                            });
                            
                            // Refresh the data to show updated status
                            await fetchRequisition();
                            
                            // Emit custom event to notify other components about the approval
                            window.dispatchEvent(new CustomEvent('requisitionApproved', {
                                detail: { 
                                    requisitionId: data.requisition_id,
                                    level: level,
                                    userId: authUser.user_id
                                }
                            }));
                        }
                        
                        // Close the modal
                        setShowApprovalModal(false);
                         
                    } catch (error) {
                        await Swal.fire({
                            title: 'Approval Failed!',
                            text: 'Approval failed: ' + (error.message || error),
                            icon: 'error',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#dc3545'
                        });
                    }
                }}
                requisition={data}
                currentUser={authUser}
                originalUser={originalUser}
                isMasquerading={!!originalUser}
            />
            {showAssignModal && data && (
                <AssignModal 
                    isOpen={showAssignModal}
                    onClose={() => setShowAssignModal(false)}
                    onSubmit={async (assignData) => {
                        try {
                            await assignRequisition(data.requisition_id, {
                                assignToUserId: assignData.assignToUserId,
                                specification_title: data.specification_title
                            });
                            await fetchRequisition();
                        } catch (error) {
                            throw error;
                        }
                    }}
                    requisition={data}
                    allUsers={data?.buyers || []}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                    data={data}
                />
            )}
            {showAmendModal && data && (
                <AmendModal 
                    isOpen={showAmendModal}
                    onClose={() => setShowAmendModal(false)}
                    onSubmit={async (amendData) => {
                        try {
                            await amendRequisition(data.requisition_id, amendData);
                            await fetchRequisition();
                        } catch (error) {
                            throw error;
                        }
                    }}
                    requisition={data}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                />
            )}
            <FilesModal 
                show={showFilesModal}
                onHide={() => setShowFilesModal(false)}
                files={data?.files || []}
                title="Requisition Files"
            />
            <AdditionalFilesModal 
                show={showAdditionalFilesModal}
                onHide={() => setShowAdditionalFilesModal(false)}
                files={data?.files || []}
                requisitionId={data?.requisition_id}
                onFilesUpdated={fetchRequisition}
            />
            {showCapexModal && data && (
                <CapexModal 
                    isOpen={showCapexModal}
                    onClose={() => setShowCapexModal(false)}
                    onSubmit={async (capexData) => {
                        try {
                            const result = await updateCapexNumber(data.requisition_id, {
                                capexNumber: capexData.capexNumber,
                                ref: data.requisition_number
                            });
                            
                            if (result.status === 1) {
                                // Show success message
                                await Swal.fire({
                                    title: 'Success!',
                                    text: result.message || 'CAPEX number updated successfully!',
                                    icon: 'success',
                                    confirmButtonText: 'OK',
                                    confirmButtonColor: '#28a745'
                                });
                                
                                // Refresh the requisition data
                                await fetchRequisition();
                            } else {
                                throw new Error(result.message || 'Failed to update CAPEX number');
                            }
                        } catch (error) {
                            throw error;
                        }
                    }}
                    requisition={data}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                />
            )}
            {showSapModal && data && (
                <SapApprovalModal 
                    isOpen={showSapModal}
                    onClose={() => setShowSapModal(false)}
                    onSubmit={async (sapData) => {
                        try {
                            const result = await requestSapApproval(data.requisition_id, sapData);
                            if (result.status === 1) {
                                await fetchRequisition();
                            } else {
                                throw new Error(result.message || 'Failed to submit SAP approval');
                            }
                        } catch (error) {
                            throw error;
                        }
                    }}
                    requisition={data}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                />
            )}

            {/* Quote Details Modal - Matching PHP functionality */}
            <QuoteModal 
                isOpen={showQuoteModal}
                onClose={() => setShowQuoteModal(false)}
                quote={selectedQuote}
                requisitionData={data}
                onEvaluate={handleEvaluateAction}
                isEvaluating={isEvaluating}
                canEvaluate={hasPermission("Evaluate Quotations") || isAdmin()}
            />

            {/* Quote View Modal - For evaluation */}
            <QuoteViewModal
                isOpen={showQuoteViewModal}
                onClose={() => setShowQuoteViewModal(false)}
                quote={selectedQuote}
                quoteId={selectedQuote?.quotation_id}
                rfqId={data?.specification_id}
                requisitionTitle={data?.specification_title}
                canMakeCorrections={hasPermission("Edit Requisitions") || isAdmin()}
                specificationId={data?.specification_id}
                requisitionId={data?.requisition_id}
                onEvaluationSave={handleQuoteEvaluation}
            />
 

            {/* Junk Modal */}
            {showJunkModal && data && (
                <JunkModal 
                    isOpen={showJunkModal}
                    onClose={() => setShowJunkModal(false)}
                    onSubmit={async (junkData) => {
                        try {
                            // Prepare masquerading context like other modals
                            const masqueradingContext = {
                                is_masquerading: !!originalUser,
                                original_user_id: originalUser?.user_id || null,
                                masqueraded_by: originalUser?.user_id || null,
                                on_behalf_of: authUser.user_id
                            };
                            
                            // Use the proper API method with masquerading context
                            await sendToJunk(data.requisition_id, junkData.junk_reason, masqueradingContext);
                            
                            // Refresh the requisition data to update the sidebar status
                            await fetchRequisition();
                            
                            // Close the modal to show the updated sidebar
                            setShowJunkModal(false);
                        } catch (error) {
                            throw error;
                        }
                    }}
                    requisition={data}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                />
            )}

            {/* Change GL Approvers Modal */}
            {showChangeGLModal && data && (
                <ChangeGLModal 
                    isOpen={showChangeGLModal}
                    onClose={() => setShowChangeGLModal(false)}
                    onSubmit={handleGLApproversUpdate}
                    requisition={data}
                    allUsers={allUsers}
                    currentUser={authUser}
                    originalUser={originalUser}
                    isMasquerading={!!originalUser}
                />
            )}
        </div>
    );
};

// Modern Items Specification Card Component - Compact
const ItemsSpecificationCard = ({ data, onViewQuote }) => {
    const { user: authUser } = useAuth(); // Get user from AuthContext
   
    // First, get the quantities to determine the expected number of items
    const qtyArray = (() => {
        const rawQty = data?.specification_qty || data?.requisition_qty || data?.procurement_qty || data?.selected?.procurement_qty;
        
        // If it's a string, split by commas and parse each quantity
        if (typeof rawQty === 'string') {
            return rawQty.split(',').map(q => q.trim()).filter(q => q.length > 0);
        }
        
        // If it's already an array, use it directly
        if (Array.isArray(rawQty)) {
            return rawQty;
        }
        
        // Try to parse as serialized data
        return parseNumericArray(rawQty) || [];
    })();
    
    // Process item sources based on the data available
    const items = (() => {
        const rawItems = data?.specification_items || data?.requisition_items || data?.procurement_items || data?.selected?.procurement_items;
        
        // If it's already an array, use it directly
        if (Array.isArray(rawItems)) {
            return rawItems;
        }
        
        // If it's a string, check for different formats
        if (typeof rawItems === 'string') {
            const trimmedItems = rawItems.trim();
            
            // First priority: Check for |~| delimiter (our new standard)
            if (trimmedItems.includes('|~|')) {
                return trimmedItems.split('|~|').map(item => item.trim()).filter(item => item.length > 0);
            }
            
            // Second priority: Parse PHP serialized format (starts with 'a:')
            if (trimmedItems.startsWith('a:')) {
                try {
                    const result = [];
                    const match = trimmedItems.match(/a:\d+:\{(.*)\}/);
                    if (match) {
                        const content = match[1];
                        const valueMatches = content.match(/s:\d+:"([^"]*)"/g);
                        if (valueMatches) {
                            valueMatches.forEach(match => {
                                const valueMatch = match.match(/s:\d+:"([^"]*)"/);
                                if (valueMatch) {
                                    result.push(valueMatch[1]);
                                }
                            });
                            return result;
                        }
                    }
                } catch (error) {
                    console.warn('Failed to parse PHP serialized items:', error);
                }
            }
            
            // Last resort: Treat entire string as a single item
            // DO NOT split by commas - items can contain commas in their descriptions
            if (trimmedItems) {
                return [trimmedItems];
            }
        }
        
        return [];
    })();
     
    
    // Use the qtyArray we already calculated
    const qty = qtyArray;
    
    // Extract and process amounts from data
    const amounts = (() => {
        const rawAmounts = data?.selected?.quotation_amounts;
        
        if (rawAmounts) {
            // If it's a string, split by commas
            if (typeof rawAmounts === 'string') {
                return rawAmounts.split(',').map(amount => amount.trim().replace(/\s+/g, '')).filter(amount => amount.length > 0);
            }
            
            // If it's already an array
            if (Array.isArray(rawAmounts)) {
                return rawAmounts.map(amount => {
                    return amount.toString().replace(/\s+/g, '');
                });
            }
        }
        
        // Fallback to numeric value for R90.00 per item
        return items.map(() => "90.00");
    })();
    
    // VATs - from data or defaults
    const vats = (() => {
        const rawVats = data?.selected?.quotation_vats;
        
        if (rawVats) {
            // If it's a string, split by commas
            if (typeof rawVats === 'string') {
                return rawVats.split(',').map(vat => vat.trim()).filter(vat => vat.length > 0);
            }
            
            // If it's already an array
            if (Array.isArray(rawVats)) {
                return rawVats;
            }
        }
        
        return items.map(() => "0.00");
    })();
    
    const units = parseArraySafely(data?.requisition_units || data?.selected?.quotation_units);
    const comments = parseArraySafely(data?.selected?.quotation_comments);

    // Check if there's actually a selected quote (not just quotes available)
    const hasSelectedQuote = !!(data?.selected?.quotation_id);

    if (!items || items.length === 0) {
        return (
            <div className="border shadow-sm rounded-lg mb-3" 
                 style={{ 
                     backgroundColor: colors.bgCard,
                     borderColor: colors.antiflashWhite 
                 }}>
                <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.primary, color: '#FFFFFF' }}>
                    <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                        <i className="fas fa-list mr-2" style={{ color: '#FFFFFF !important' }}></i>
                        Specification Items
                    </h6>
                </div>
                <div className="text-center py-6 px-4">
                    <div style={{ color: colors.textMuted }}>
                        <i className="fas fa-inbox text-3xl mb-3 opacity-50"></i>
                        <p className="text-sm font-medium">No items found for this requisition.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="border shadow-sm rounded-lg mb-3" 
             style={{ 
                 backgroundColor: colors.bgCard,
                 borderColor: colors.antiflashWhite 
             }}>
            <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.cardInfo, color: '#FFFFFF' }}>
                <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                    <i className="fas fa-list mr-2" style={{ color: '#FFFFFF !important' }}></i>
                    Specification Items ({items.length})
                </h6>
            </div>
            <div className="p-0">
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Item Description
                                </th>
                                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity
                                </th>
                                {hasSelectedQuote && (
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Unit Price
                                    </th>
                                )}
                                {hasSelectedQuote && (
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        VAT
                                    </th>
                                )}
                                {hasSelectedQuote && (
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Line Total
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.map((item, index) => {
                                // quotation_amounts contains LINE SUBTOTALS (qty × unit price), not unit prices
                                const lineSubtotal = (amounts && amounts[index]) ? parseFloat(amounts[index]) || 0 : 0;
                                const quantity = (qty && qty[index]) ? parseFloat(qty[index]) || 0 : 0;
                                const vat = (vats && vats[index]) ? parseFloat(vats[index]) || 0 : 0;
                                
                                // Calculate unit price from line subtotal ÷ quantity
                                const unitPrice = quantity > 0 ? lineSubtotal / quantity : 0;
                                
                                // Line total = line subtotal + VAT
                                const lineTotal = lineSubtotal + vat;
                                
                                return (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-3 py-2 text-sm text-gray-900">
                                            <div className="font-medium">{item || 'N/A'}</div>
                                            {comments && comments[index] && (
                                                <div className="text-xs text-gray-500 mt-1">{comments[index]}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 text-center">
                                            {quantity}
                                        </td>
                                        {hasSelectedQuote && (
                                            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">
                                                R{unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        )}
                                        {hasSelectedQuote && (
                                            <td className="px-3 py-2 text-sm text-gray-500 text-right">
                                                {vat > 0 ? `R${vat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                            </td>
                                        )}
                                        {hasSelectedQuote && (
                                            <td className="px-3 py-2 text-sm text-blue-600 text-right font-bold">
                                                R{lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                        
                        {/* Summary rows - only show when there's a selected quote */}
                        {hasSelectedQuote && (
                            <tbody className="bg-blue-50 border-t-2 border-blue-300">
                                <tr>
                                    <td colSpan="2" className="px-3 py-2 text-sm font-bold text-blue-900 text-right">
                                        Grand Total:
                                    </td>
                                    <td colSpan="3" className="px-3 py-2 text-lg font-bold text-blue-600 text-right">
                                        R{(() => {
                                            // If we have selected quotation total, use it after formatting
                                            if (data?.selected?.quotation_total) {
                                                const cleanTotal = data.selected.quotation_total.toString().replace(/\s+/g, '');
                                                if (!isNaN(parseFloat(cleanTotal))) {
                                                    return parseFloat(cleanTotal).toLocaleString('en-US', { 
                                                        minimumFractionDigits: 2, 
                                                        maximumFractionDigits: 2 
                                                    });
                                                }
                                            }
                                            
                                            // Calculate from amounts if available
                                            if (amounts && amounts.length > 0) {
                                                const total = amounts.reduce((sum, amount) => {
                                                    return sum + (parseFloat(amount.toString().replace(/\s+/g, '')) || 0);
                                                }, 0);
                                                return total.toLocaleString('en-US', { 
                                                    minimumFractionDigits: 2, 
                                                    maximumFractionDigits: 2 
                                                });
                                            }
                                            
                                            return "0.00";
                                        })()}
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>

                {/* VAT Summary - matching PHP logic */}
                {parseFloat(data?.selected?.quotation_vat || 0) > 0 && (
                    <div className="p-3 border-t" style={{ backgroundColor: colors.antiflashWhite, borderColor: colors.ashGray }}>
                        <h6 className="mb-2 text-sm" style={{ color: colors.primary }}>
                            <i className="fas fa-calculator mr-2"></i>
                            Tax Summary
                        </h6>
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-sm text-left">Rate</th>
                                    <th className="text-sm text-left">Tax</th>
                                    <th className="text-sm text-left">Net</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="text-sm">VAT @ 15%</td>
                                    <td className="text-sm">R{parseFloat(data.selected.quotation_vat || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="text-sm">R{(parseFloat(data.selected.quotation_total || 0) - parseFloat(data.selected.quotation_vat || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Quote Actions */}
                {data?.selected?.quotation_id && (
                    <div className="p-3 border-t">
                        <div className="flex justify-between items-center">
                            <div>
                                <h6 className="mb-1 text-green-600 text-sm">Selected Quotation</h6>
                                <p className="text-gray-500 text-sm mb-0">
                                    {data.selected.quotation_company_name} • {data.quote_files || 0} files
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="secondary" 
                                    className="compact-button"
                                    onClick={() => onViewQuote(data.selected)}
                                    icon={FaEye}
                                >
                                    View
                                </Button>
                                {(( authUser?.is_procurement) && data?.selected?.quotation_id) && (
                                    <Button 
                                        variant="secondary" 
                                        className="compact-button"
                                        onClick={() => window.location.href = `/requisitions/edit_requisition/${data.requisition_id}`}
                                        icon={FaEdit}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Quotations Summary Card Component - Compact version
const QuotationsSummaryCard = ({ submissions, selectedQuoteId, onViewQuote }) => (
    <div className="border shadow-sm rounded-lg mb-3" 
         style={{ 
             backgroundColor: colors.bgCard,
             borderColor: colors.antiflashWhite 
         }}>
        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.primary, color: '#FFFFFF' }}>
            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                <i className="fas fa-file-invoice-dollar mr-2" style={{ color: '#FFFFFF !important' }}></i>
                Quotations Received ({submissions.length})
            </h6>
        </div>
        <div className="p-0">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b" 
                           style={{ 
                               backgroundColor: colors.antiflashWhite,
                               borderColor: colors.ashGray 
                           }}>
                        <tr>
                            <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Supplier Name</th>
                            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Evaluation</th>
                            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Score</th>
                            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Quote</th>
                            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Actions</th>
                            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider" 
                                style={{ color: colors.textSecondary }}>Selected</th>
                        </tr>
                    </thead>
                    <tbody style={{ backgroundColor: colors.bgCard }}>
                        {submissions.map((quote, index) => (
                            <tr key={index} 
                                className={selectedQuoteId == quote.quotation_id ? 'border-b' : 'hover:opacity-75 transition-opacity border-b'}
                                style={{ 
                                    backgroundColor: selectedQuoteId == quote.quotation_id ? colors.cambridgeBlue + '20' : 'transparent',
                                    borderColor: selectedQuoteId == quote.quotation_id ? colors.cambridgeBlue : colors.antiflashWhite
                                }}>
                                <td className="py-3 px-3">
                                    <div className="font-semibold" style={{ color: colors.textPrimary }}>{quote.quotation_company_name}</div>
                                </td>
                                <td className="py-3 text-center">
                                    {/* Check if quote has evaluation data using robust detection logic */}
                                    {(quote.quotation_evaluation && 
                                      quote.quotation_evaluation !== null && 
                                      quote.quotation_evaluation !== '' && 
                                      quote.quotation_evaluation !== 'null' && (
                                          // Check if values object exists and has actual scores
                                          (quote.values && 
                                           typeof quote.values === 'object' && 
                                           (quote.values.quality || quote.values.adherence || 
                                            quote.values.delivery || quote.values.price)) ||
                                          // Or check if values array exists and has actual scores
                                          (quote.values && 
                                           Array.isArray(quote.values) && 
                                           quote.values.length > 0) ||
                                          // Or check if quotation_evaluation is a valid JSON string
                                          (typeof quote.quotation_evaluation === 'string' && 
                                           (quote.quotation_evaluation.startsWith('{') || 
                                            quote.quotation_evaluation.startsWith('[')))
                                      )) ? (
                                        <div>
                                            <span className="px-2 py-1 text-xs font-medium rounded-full border mb-2 inline-block" 
                                                  style={{
                                                      backgroundColor: colors.cambridgeBlue + '20',
                                                      color: colors.cambridgeBlue,
                                                      borderColor: colors.cambridgeBlue
                                                  }}>Evaluated</span>
                                            <div className="text-xs space-y-1" style={{ color: colors.textSecondary }}>
                                                {quote.values && typeof quote.values === 'object' && 
                                                 !Array.isArray(quote.values) ? (
                                                    <div className="grid grid-cols-1 gap-1">
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Quality:</div>
                                                            <div>{quote.values.quality || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Spec:</div>
                                                            <div>{quote.values.adherence || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Delivery:</div>
                                                            <div>{quote.values.delivery || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Price:</div>
                                                            <div>{quote.values.price || 0}</div>
                                                        </div>
                                                    </div>
                                                ) : quote.values && Array.isArray(quote.values) ? (
                                                    <div className="grid grid-cols-1 gap-1">
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Quality:</div>
                                                            <div>{quote.values[0] || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Spec:</div>
                                                            <div>{quote.values[1] || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Delivery:</div>
                                                            <div>{quote.values[2] || 0}</div>
                                                        </div>
                                                        <div style={{ display: 'flex' }}>
                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Price:</div>
                                                            <div>{quote.values[3] || 0}</div>
                                                        </div>
                                                    </div>
                                                ) : typeof quote.quotation_evaluation === 'string' ? (
                                                    (() => {
                                                        // Parse evaluation string to get scores
                                                        try {
                                                            // Try to parse the evaluation string
                                                            let evalScores = JSON.parse(quote.quotation_evaluation);
                                                            if (Array.isArray(evalScores) && evalScores.length >= 4) {
                                                                return (
                                                                    <div className="grid grid-cols-1 gap-1">
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Quality:</div>
                                                                            <div>{evalScores[0]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Spec:</div>
                                                                            <div>{evalScores[1]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Delivery:</div>
                                                                            <div>{evalScores[2]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Price:</div>
                                                                            <div>{evalScores[3]}</div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        } catch (e) {
                                                            // If JSON parsing fails, extract scores from string format
                                                            const match = quote.quotation_evaluation.match(/\[(\d+),(\d+),(\d+),(\d+)\]/);
                                                            if (match && match.length === 5) {
                                                                return (
                                                                    <div className="grid grid-cols-1 gap-1">
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Quality:</div>
                                                                            <div>{match[1]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Spec:</div>
                                                                            <div>{match[2]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Delivery:</div>
                                                                            <div>{match[3]}</div>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <div style={{ width: '60px', textAlign: 'right', marginRight: '5px' }}>Price:</div>
                                                                            <div>{match[4]}</div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        }
                                                        return null;
                                                    })()
                                                ) : null}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="px-2 py-1 text-xs font-medium rounded-full border" 
                                              style={{
                                                  backgroundColor: colors.warning + '20',
                                                  color: colors.warning,
                                                  borderColor: colors.warning
                                              }}>Pending</span>
                                    )}
                                </td>
                                <td className="py-3 text-center">
                                    <span className="px-2 py-1 text-xs font-medium rounded-full border" 
                                          style={{
                                              backgroundColor: colors.primary + '20',
                                              color: colors.primary,
                                              borderColor: colors.primary
                                          }}>
                                        {(() => {
                                            // Check for values array
                                            if (quote.values && Array.isArray(quote.values)) {
                                                return parseInt(quote.values[0] || 0) + parseInt(quote.values[1] || 0) + 
                                                      parseInt(quote.values[2] || 0) + parseInt(quote.values[3] || 0);
                                            }
                                            
                                            // Check for values object
                                            if (quote.values && typeof quote.values === 'object' && !Array.isArray(quote.values)) {
                                                return parseInt(quote.values.quality || 0) + parseInt(quote.values.adherence || 0) + 
                                                      parseInt(quote.values.delivery || 0) + parseInt(quote.values.price || 0);
                                            }
                                            
                                            // Check for evaluation string
                                            if (typeof quote.quotation_evaluation === 'string') {
                                                try {
                                                    // Try to parse JSON
                                                    const evalScores = JSON.parse(quote.quotation_evaluation);
                                                    if (Array.isArray(evalScores)) {
                                                        return evalScores.reduce((sum, score) => sum + (parseInt(score) || 0), 0);
                                                    }
                                                } catch (e) {
                                                    // If JSON parsing fails, extract scores from string format
                                                    const match = quote.quotation_evaluation.match(/\[(\d+),(\d+),(\d+),(\d+)\]/);
                                                    if (match && match.length === 5) {
                                                        return parseInt(match[1] || 0) + parseInt(match[2] || 0) + 
                                                              parseInt(match[3] || 0) + parseInt(match[4] || 0);
                                                    }
                                                }
                                            }
                                            
                                            // Fall back to value property if available
                                            return quote.value || 0;
                                        })()}/16
                                    </span>
                                </td>
                                <td className="py-3 text-center font-bold" style={{ color: colors.textPrimary }}>
                                    {(() => {
                                        let total = 0;
                                        
                                        // Try to parse the quotation_total
                                        if (quote.quotation_total) {
                                            // Remove any spaces in the value
                                            const cleanTotal = quote.quotation_total.toString().replace(/\s+/g, '');
                                            if (!isNaN(parseFloat(cleanTotal))) {
                                                total = parseFloat(cleanTotal);
                                            }
                                        }
                                        
                                        // Format the number with thousand separators
                                        return `R${total.toLocaleString('en-US', { 
                                            minimumFractionDigits: 2, 
                                            maximumFractionDigits: 2 
                                        })}`;
                                    })()}
                                </td>
                                <td className="py-3 text-center">
                                    <Button 
                                        variant="secondary" 
                                        className="compact-button"
                                        onClick={() => onViewQuote(quote)}
                                        icon={FaEye}
                                    >
                                        View
                                    </Button>
                                </td>
                                <td className="py-3 text-center">
                                    {selectedQuoteId == quote.quotation_id ? (
                                        <span className="px-3 py-1 text-xs font-medium rounded-full border" 
                                              style={{
                                                  backgroundColor: colors.cambridgeBlue + '20',
                                                  color: colors.cambridgeBlue,
                                                  borderColor: colors.cambridgeBlue
                                              }}>
                                            <i className="fas fa-check mr-1"></i>
                                            Selected
                                        </span>
                                    ) : (
                                        <span style={{ color: colors.textMuted }}>-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

// Specification Details Card - Compact
const SpecificationDetailsCard = ({ specificationDetails }) => {
    // Check if specification details exist and are not empty
    if (!specificationDetails || 
        (typeof specificationDetails === 'string' && specificationDetails.trim() === '') ||
        specificationDetails === null ||
        specificationDetails === undefined) {
        return null; // Don't render the card if no details
    }

    return (
        <div className="border shadow-sm rounded-lg mb-3" 
             style={{ 
                 backgroundColor: colors.bgCard,
                 borderColor: colors.antiflashWhite 
             }}>
            <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.cardInfo, color: '#FFFFFF' }}>
                <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                    <i className="fas fa-clipboard-list mr-2" style={{ color: '#FFFFFF !important' }}></i>
                    Specification Details
                </h6>
            </div>
            <div className="p-4">
                <div className="text-sm" 
                     style={{ color: colors.textPrimary }}
                     dangerouslySetInnerHTML={{ 
                         __html: typeof specificationDetails === 'string' 
                             ? specificationDetails 
                             : String(specificationDetails)
                     }}>
                </div>
            </div>
        </div>
    );
};

// Motivation Card - Compact
const MotivationCard = ({ motivation }) => (
    <div className="border shadow-sm rounded-lg h-full" 
         style={{ 
             backgroundColor: colors.bgCard,
             borderColor: colors.antiflashWhite 
         }}>
        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.cardInfo, color: '#FFFFFF' }}>
            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                <i className="fas fa-comment mr-2" style={{ color: '#FFFFFF !important' }}></i>
                Motivation for fewer quotes
            </h6>
        </div>
        <div className="p-4">
            <div className="text-sm" style={{ color: colors.textPrimary }}>
                {motivation}
            </div>
        </div>
    </div>
);

// Declaration Card - Compact
const DeclarationCard = ({ declaration }) => (
    <div className="border shadow-sm rounded-lg h-full" 
         style={{ 
             backgroundColor: colors.bgCard,
             borderColor: colors.antiflashWhite 
         }}>
        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.cardWarning, color: '#FFFFFF' }}>
            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                <i className="fas fa-gavel mr-2" style={{ color: '#FFFFFF !important' }}></i>
                Declaration
            </h6>
        </div>
        <div className="p-4">
            <div className="text-sm" style={{ color: colors.textPrimary }}>
                {declaration}
            </div>
        </div>
    </div>
);

// Supporting Documents Card - Updated for standardized file handling
const SupportingDocumentsCard = ({ files }) => (
    <div className="border shadow-sm rounded-lg mb-3" 
         style={{ 
             backgroundColor: colors.bgCard,
             borderColor: colors.antiflashWhite 
         }}>
        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.cardSuccess, color: '#FFFFFF' }}>
            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                <i className="fas fa-paperclip mr-2" style={{ color: '#FFFFFF !important' }}></i>
                Supporting Documents ({files.length})
            </h6>
        </div>
        <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {files.map((file, index) => {
                    // Use standardized attachment system
                    const filePath = file.file_path;
                    const fileName = file.file_name;
                    const fileUrl = `${API_BASE_URL}/api/attachments/requisitions/${filePath}`;
                    
                    return (
                        <div key={file.attachment_id || index}>
                            <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors" 
                                 style={{
                                     backgroundColor: colors.antiflashWhite,
                                     borderColor: colors.ashGray
                                 }}>
                                <div className="flex items-center flex-grow" style={{ minWidth: 0 }}>
                                    <div className="mr-3">
                                        <i className="fas fa-file-alt text-xl" style={{ color: colors.primary }}></i>
                                    </div>
                                    <div className="flex-grow" style={{ minWidth: 0 }}>
                                        <div className="font-semibold text-sm" 
                                             style={{ 
                                                 color: colors.textPrimary,
                                                 overflow: 'hidden',
                                                 textOverflow: 'ellipsis',
                                                 whiteSpace: 'nowrap'
                                             }}
                                             title={fileName}>
                                            {fileName}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {file.file_size ? `${Math.round(file.file_size / 1024)} KB` : ''}
                                            {file.uploaded_by_name ? ` • ${file.uploaded_by_name}` : ''}
                                            {file.uploaded_at ? ` • ${new Date(file.uploaded_at).toLocaleDateString()}` : ''}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex space-x-2 ml-3">
                                    {/* View button */}
                                    <button
                                        onClick={() => window.open(fileUrl + '?v=' + Date.now(), '_blank')}
                                        className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                        title="View"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                    {/* Download button */}
                                    <button
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = fileUrl + '?download=1';
                                            link.download = fileName;
                                            link.click();
                                        }}
                                        className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                        title="Download"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    </div>
);

// Feedback Section Component - Compact
const FeedbackSection = ({ comments }) => (
    <div className="border shadow-sm rounded-lg mb-3" 
         style={{ 
             backgroundColor: colors.bgCard,
             borderColor: colors.antiflashWhite 
         }}>
        <div className="rounded-t-lg px-4 py-3 card-header-white" style={{ background: gradients.primary, color: '#FFFFFF' }}>
            <h6 className="mb-0 font-bold text-sm" style={{ color: '#FFFFFF !important' }}>
                <i className="fas fa-comments mr-2" style={{ color: '#FFFFFF !important' }}></i>
                Feedback & Comments ({comments.length})
            </h6>
        </div>
        <div className="p-0">
            {comments.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b" 
                               style={{ 
                                   backgroundColor: colors.antiflashWhite,
                                   borderColor: colors.ashGray 
                               }}>
                            <tr>
                                <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: '#495057' }}>User</th>
                                <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: '#495057' }}>Comment</th>
                                <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: '#495057' }}>Date</th>
                            </tr>
                        </thead>
                        <tbody style={{ backgroundColor: colors.bgCard }}>
                            {comments.map((comment, index) => (
                                <tr key={index} className="border-b hover:opacity-75 transition-opacity"
                                    style={{ borderColor: colors.antiflashWhite }}>
                                    <td className="py-3 px-3">
                                        <div className="font-semibold" style={{ color: colors.textPrimary, marginBottom: '4px' }}>
                                            {comment.user_full_name}
                                        </div>
                                        <span className="px-2 py-1 text-xs font-medium rounded-full border"
                                            style={{
                                                backgroundColor: colors.bgCard,
                                                color: colors.textPrimary,
                                                borderColor: colors.ashGray
                                            }}>
                                            {comment.department_name}
                                        </span>
                                    </td>
                                    <td className="py-3 px-3" style={{ color: colors.textPrimary }}>
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1">
                                                <div dangerouslySetInnerHTML={{ __html: comment.comment_text }} />
                                            </div>
                                            {comment.comment_approved == '1' && (
                                                <span className="px-2 py-1 text-xs font-medium rounded-full border flex items-center gap-1"
                                                    style={{
                                                        backgroundColor: '#d4edda',
                                                        color: '#155724',
                                                        borderColor: '#c3e6cb'
                                                    }}>
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    Approval
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                        {(() => {
                                            const date = new Date(comment.comment_date * 1000);
                                            const day = date.getDate().toString().padStart(2, '0');
                                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                            const year = date.getFullYear();
                                            const hours = date.getHours().toString().padStart(2, '0');
                                            const minutes = date.getMinutes().toString().padStart(2, '0');
                                            return (
                                                <>
                                                    <div>{`${day}-${month}-${year}`}</div>
                                                    <div>{`${hours}:${minutes}`}</div>
                                                </>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-6">
                    <div style={{ color: '#6c757d' }}>
                        <i className="fas fa-comment-slash text-3xl mb-3 opacity-50"></i>
                        <p className="text-sm font-medium">No comments or feedback yet.</p>
                    </div>
                </div>
            )}
        </div>
    </div>
);
// Action Panel Component - Matching PHP sidebar actions exactly
const ActionPanel = ({ 
    data, 
    onAction, 
    canUserApprove, 
    hasPermission, 
    isOwner, 
    isDepartmentUser, 
    isAdmin,
    hasErrors,
    handleApproval,
    requiresPMApproval
}) => {
    const { user: authUser, originalUser } = useAuth(); // Get user and originalUser from AuthContext
 
    
    // Check if user can send to junk - based on PHP logic: procurement OR creator OR same department
    const canSendToJunk = () => {
        const isBuyer = authUser?.role == 6 || authUser?.role_id == 6;
        const isCreator = authUser?.user_id == data?.requisition_user;
        const isSameDepartment = authUser?.department == data?.department_id;
        
        return isBuyer || isCreator || isSameDepartment;
    };

    // Check if user can request review - same conditions as canSendToJunk plus awaiting approval
    const canRequestReview = () => {
        // Same visibility conditions as canSendToJunk
        const isBuyer = authUser?.role == 6 || authUser?.role_id == 6;
        const isCreator = authUser?.user_id == data?.requisition_user;
        const isSameDepartment = authUser?.department == data?.department_id;
        
        const hasVisibility = isBuyer || isCreator || isSameDepartment;
        
        // Check if requisition is ACTIVELY in approval stage
        // The requisition must have passed all prerequisites (no blockers)
        // Check for prerequisite errors: quotation, evaluation, declaration, PM approval
        const hasBlockers = (() => {
            // Check quotation requirement
            if (data?.specification_emergency == 1 && !data?.requisition_quote) {
                return true;
            }
            
            // Check if quotation is evaluated
            const hasEvaluation = hasAnyQuotationEvaluation(data);
            if (!hasEvaluation && data?.submissions && data.submissions.length > 0) {
                return true;
            }
            
            // Check declaration
            if (!data?.requisition_declaration) {
                return true;
            }
            
            // Check PM approval if required
            if (requiresPMApproval(data) && data?.requisition_pm_approved != 1) {
                return true;
            }
            
            return false;
        })();
        
        // Not fully approved yet
        const notFullyApproved = data?.requisition_status < data?.specification_approvals;
        
        // Not rejected
        const notRejected = data?.requisition_status != 4;
        
        // In active approval stage (status 0, 1, or 2 representing 1st, 2nd, 3rd approval stages)
        const inApprovalStage = [0, 1, 2].includes(data?.requisition_status);
        
        // Show button only when: has visibility, no blockers, not fully approved, not rejected, and in approval stage
        return hasVisibility && !hasBlockers && notFullyApproved && notRejected && inApprovalStage;
    };
    
    // Capex Number Logic - EXACT PHP MATCH
    const needsCapex = () => {
        const isBuyer = authUser?.role == 6 || authUser?.role_id == 6;
        const isCreator = authUser?.user_id == data?.requisition_user;
        const isSameDepartment = authUser?.department == data?.department_id;
        
        const hasVisibility = isBuyer || isCreator || isSameDepartment;
        return (hasVisibility && ["100030-03-01-01", "100031-03-01-01"].includes(data?.gl_code?.trim()) && !data?.requisition_capex) ||
               (hasVisibility && ["100030-03-01-01", "100031-03-01-01"].includes(data?.gl_code?.trim()));
    };

    // Error checking functions
    const needsQuotation = () => {
        return data?.specification_emergency == 1 && data?.requisition_quote == null;
    };

    const needsEvaluation = () => {
        return !hasAnyQuotationEvaluation(data);
    };

    const needsDeclaration = () => {
        return data?.requisition_declaration == "";
    };

    // If rejected - Now handled by ProcessFlowSidebar, no need for duplicate
    // if (data?.requisition_status == 4) {
    //     return (
    //         <Card className="border-0 shadow-lg rounded-4 mt-4" 
    //               style={{ borderColor: colors.antiflashWhite }}>
    //             <Card.Header className="py-3" style={{ background: `linear-gradient(135deg, ${colors.danger}, ${colors.ashGray})`, color: colors.textWhite }}>
    //                 <h6 className="mb-0 fw-bold">
    //                     <i className="fas fa-times-circle me-2"></i>
    //                     Status: Rejected
    //                 </h6>
    //             </Card.Header>
    //             <Card.Body className="p-4">
    //                 <Alert variant="danger" className="mb-0">
    //                     <FaInfo className="me-2" />
    //                     This requisition has been rejected and cannot be resubmitted.
    //                 </Alert>
    //             </Card.Body>
    //         </Card>
    //     );
    // }

    return (
        <Card className="border-0 shadow-sm compact-card" 
              style={{ borderColor: colors.antiflashWhite }}>
            <Card.Header className="compact-header" 
                         style={{ 
                             background: '#3697E1', 
                             color: '#FFFFFF' 
                         }}>
                <h6 className="mb-0 fw-bold" style={{ color: '#FFFFFF', fontSize: '1rem' }}>
                    <i className="fas fa-cogs me-2" style={{ color: '#FFFFFF' }}></i>
                    Available Actions
                </h6>
            </Card.Header>
            <Card.Body className="compact-body" style={{ backgroundColor: '#ffffff' }}>
                {/* Additional Files - using original logic plus approver condition */}
                {data?.requisition_status < data?.specification_approvals && data.requisition_po_date == 0 && data.requisition_amender_id == 0 && 
                 (authUser?.role == 6 || isOwner() || isDepartmentUser() || isAdmin() || 
                  canUserApprove('first') || canUserApprove('second') || canUserApprove('third')) && (
                    <div className="mb-3">
                        <Button 
                            variant="info" 
                            className="w-100 compact-button"
                            onClick={onAction.additionalFiles}
                            icon={FaFileAlt}
                            style={{ 
                                backgroundColor: '#3498db',
                                color: '#ffffff',
                                fontWeight: 'bold'
                            }}
                        >
                            Additional Files
                        </Button>
                    </div>
                )}

                {/* Capex Number - First Priority */}
                {data?.requisition_status < data?.specification_approvals && needsCapex() && (
                    <div className="mb-3">
                        <Button 
                            variant="primary" 
                            className="w-100 compact-button"
                            onClick={onAction.capexNumber}
                            icon={FaFileAlt}
                        >
                            Capex Number
                        </Button>
                    </div>
                )}

                {/* Post-Approval Actions */}
                {data?.requisition_status >= data?.specification_approvals && (
                    <div className="mb-3">
                        <h6 className="mb-2 fw-bold small" style={{ color: '#495057', fontSize: '0.9rem' }}>
                            <i className="fas fa-check-circle me-2" style={{ color: '#28a745' }}></i>
                            Post-Approval Actions
                        </h6>
                        
                        {/* Download Requisition */}
                        <div className="mb-2">
                            <Button 
                                variant="primary" 
                                className="w-100 compact-button"
                                onClick={onAction.downloadRequisition}
                                icon={FaDownload}
                            >
                                Download Requisition
                            </Button>
                        </div>



                        {/* View Files */}
                        <div className="mb-2">
                            <Button 
                                variant="secondary" 
                                className="w-100 compact-button"
                                onClick={onAction.viewFiles}
                                icon={FaEye}
                            >
                                View Files
                            </Button>
                        </div>

                                  {/* Send to Junk - Available for procurement, creator, or same department */}
                        {canSendToJunk() && (
                            <div className="mb-2">
                                <Button 
                                    variant="danger" 
                                    className="w-100 compact-button"
                                    onClick={onAction.sendToJunk}
                                    icon={FaTrash}
                                >
                                    Send to Junk
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Additional Actions - Hide when fully approved */}
                {data?.requisition_status < data?.specification_approvals && (
                    <div className="mb-2">  
                        
                        {/* Change GL Information */}
                        {hasPermission("Change GL Approvers") && (
                            <div className="mb-2">
                                <Button 
                                    variant="secondary" 
                                    className="w-100 compact-button"
                                    onClick={onAction.changeGL}
                                    icon={FaEdit}
                                >
                                    Change GL Approvers
                                </Button>
                            </div>
                        )}
     
                        {/* Additional Information Required */}
                        {data.requisition_po_date == 0 && data.requisition_amender_id == 0 && 
                         data.requisition_status == data.specification_approvals && hasPermission("Assign Requisition") && (
                            <div className="mb-2">
                                <Button 
                                    variant="secondary" 
                                    className="w-100 compact-button"
                                    onClick={onAction.requestCorrection}
                                    icon={FaCommentDots}
                                >
                                    Additional Information Required
                                </Button>
                            </div>
                        )}
 
                        
                        {/* Send to Junk - Available for procurement, creator, or same department */}
                        {canSendToJunk() && (
                            <div className="mb-2">
                                <Button 
                                    variant="danger" 
                                    className="w-100 compact-button"
                                    onClick={onAction.sendToJunk}
                                    icon={FaTrash}
                                >
                                    Send to Junk
                                </Button>
                            </div>
                        )}

                        {/* Request Review - Resend approval request email to current approver */}
                        {canRequestReview() && (
                            <div className="mb-2">
                                <Button 
                                    variant="warning" 
                                    className="w-100 compact-button"
                                    onClick={onAction.requestReview}
                                    icon={FaEnvelope}
                                >
                                    Resend Approval Email
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};
 
export default ViewRequisition;
